import { AppContext } from "App";
import { debounce } from "lodash.throttle";

import { LazyLoadingSectionLogger, LoggingSection } from "@multiweb/logging";
import { IAttrValue, IOWTextAttributeDescription } from "@multiweb/types/contracts";
import { StandardOperationRights } from "Global/Base/Rights/StandardOperationRights";
import { ObjectUtils } from "Global/Base/Utils/ObjectUtils";
import { AttributeDescription } from "OW.Remoting/Remoting/Cache/ClassDescriptionCache/Interfaces";
import { IControlWithXGAction } from "UI/CommonUIInterfaces";
import { ErrorHandler } from "UI/Errors";
import { MEOWObject } from "UI/UI.Controls/DataSource/MultiEdit/MEOWObject";
import { FirmlyDisabledRights } from "UI/UI/Rights/FirmlyDisabledRights";
import { EncAttrValue } from "../../../../Global/Base/DataTypes/EncAttrValue";
import { IEncAttrValue } from "../../../../Global/Base/DataTypes/IAttrValue";
import { DIContainer } from "../../../../Global/Base/DIContainer";
import { Cursor } from "../../../../Global/Base/Enums";
import { Event, IListener, StatefulEvent } from "../../../../Global/Base/Events";
import { InvalidOperationException } from "../../../../Global/Base/Exceptions";
import { IDisposable } from "../../../../Global/Base/IDisposable";
import { CancelError, CancellationToken, CancellationTokenSource } from "../../../../Global/Base/Promises/CancellationTokenSource";
import { DeferredStateful, PromiseUtils } from "../../../../Global/Base/Promises/PromiseUtils";
import { IRightsManager } from "../../../../Global/Base/Rights/IRightsManager";
import { IItemRights, IItemRightsCollection, RightLevel, RightType } from "../../../../Global/Base/Rights/RightTypes";
import { StringUtils } from "../../../../Global/Base/Utils/StringUtils";
import { ValidationErrorType } from "../../../../OW.Remoting/Basic/EOWExceptionsInterfaces";
import { EOWValidateException } from "../../../../OW.Remoting/Basic/OWExceptions";
import { AttrValueReader } from "../../../../OW.Remoting/Remoting/AttrValueReader";
import { ModelDescriptionsManager } from "../../../../OW.Remoting/Remoting/Cache/ClassDescriptionCache/ModelDescriptionsManager";
import { IOWObject } from "../../../../OW.Remoting/Remoting/DataTypes/IOWObject";
import { IValidationDataObject } from "../../../../OW.Remoting/Remoting/DataTypes/IValidationDataObject";
import { OWObject } from "../../../../OW.Remoting/Remoting/DataTypes/OWObject";
import { IWorkflowMenuItem } from "../../../../OW.Remoting/Remoting/IWorkflowManager";
import { Localization } from "../../../../OW.Remoting/Remoting/Localization";
import { OWManager } from "../../../../OW.Remoting/Remoting/OWManager";
import { PasswordManager } from "../../../../OW.Remoting/Remoting/PasswordManager";
import { RequestContext } from "../../../../OW.Remoting/Remoting/RequestContext";
import { ClassItemType, IClassItem, IClassItemRights, IEvaluatedConditionResult } from "../../../../OW.Remoting/Remoting/Rights";
import { IBeforeSaveAwaitable, IBussyAwaitable, IDataLoadingAwaitable, IValidatable, IValidatableContainer, IValidationResult } from "../../../UI.BaseControls/ControlInterfaces";
import { GenericControl } from "../../../UI.BaseControls/GenericControl";
import { ActionType, IWFAction } from "../../../UI.Controls/Actions/IAction";
import { MenuType } from "../../../UI.Controls/Actions/MenuType";
import { SaveResultData } from "../../../UI.Controls/DataSource/EditableDBDataSource";
import { ElementUtils } from "../../../UI.ElementarControls/Helpers/ElementUtils";
import { ComponentTree, IBuildComponentTreeParameters } from "../../LogicalTree/ComponentTree";
import { ComponentTreeType } from "../../LogicalTree/Definitions";
import { ComponentRightsExtractor } from "../../Rights/ComponentRightsExtractor";
import { RightsManagerType } from "../../Rights/Definitions";
import { ControlRightTypesGroup, RightGroups } from "../../Rights/RightGroups";
import { convertContextFromFrameToRequest } from "../../Utilities/SharedUIMethods";
import { helper } from "../AlignPanelWidthCounter";
import { Control } from "../Controls/Control";
import {
    FormControlParams,
    IAttributeControl,
    ICompositesControl,
    IControl,
    ICursorValueAdjustable,
    IEditableControl,
    IFilterInfo,
    IGenericControl,
    IHasEncryptionState,
    IHTMLElementWithControl,
    IInfoProvidingControl,
    IMaxLength,
    IRegisterFormUIControl,
    IRequiresActualObject,
    IRequiresClassName,
    ISetValueAfterClickComponent,
    IStaticControl,
    IValidateRecommendedControl,
    IVersioned,
} from "../Controls/ControlInterfaces";
import { EditableControl } from "../Controls/EditableControl";
import { LOVEdit } from "../Controls/LOVEdit";
import { Registrator } from "../Controls/Registrator";
import { TextLOVEdit } from "../Controls/TextLOVEdit";
import { IMenu } from "../FormUI/FormUI";
import { ILoader } from "../ILoader";
import { UIManager } from "../UIManager";
import { WindowState } from "../UIManagerInterfaces";
import { DetailFormAction, OpenModeType } from "./Actions/DetailFormAction";
import { MethodAction } from "./Actions/MethodAction";
import { NodeAction } from "./Actions/NodeAction";
import { StandardAction } from "./Actions/StandardAction";
import { XGAction } from "./Actions/XGAction";
import { XGButton } from "./Actions/XGButton";
import { IControlInfoObject } from "./ControlInfo";
import { DetailFrameRightsOverrides } from "./DetailFrameRightsOverrides";
import { IValidatableFrame } from "./FormControl";
import { HideButtons, IActualObjectChangedEventArgs, IDomStructureElement } from "./Frames";
import {
    EditStyleType,
    IEditStyleMasterableFrame,
    IFrameWithKeyboardNavigation,
    IMasterableFrame,
    IMasterableFrameWithDataGrid,
    IMultiRecordsComponent,
    IObjectListEdit,
} from "./Frames/FrameInterfaces";
import { FrameSharedMethods } from "./Frames/FrameSharedMethods";
import { IDetailFrame, IDetailMasterableFrame } from "./Frames/IDetailMasterableFrame";
import { IEditableFrame } from "./Frames/IEditableFrame";
import { ITwoListFrame } from "./Frames/ITwoListFrame";
import { MasterableFormControl } from "./Frames/MasterableFormControl";
import { MasterableFrame } from "./Frames/MasterableFrame";
import { IExtendedLifeCycle } from "./IExtendedLifeCycle";
import { IForm } from "./IForm";
import { IFrame, IFrameCtxMenuData } from "./IFrame";
import { Page } from "./TabPages";
import { IValidatableComponentData, IValidationData, ValidationManager } from "./ValidationManager";

const logger = LazyLoadingSectionLogger.create(LoggingSection.detailFrame);

export class DetailFrame extends MasterableFormControl implements IValidateRecommendedControl, IValidatableContainer, IDetailFrame, IEditableFrame, IBussyAwaitable, IExtendedLifeCycle, IInfoProvidingControl, IMultiRecordsComponent, IVersioned, ICompositesControl {
    public static readonly DEBOUNCE_TIME = 100;
    //#region Fields
    /** Tlačítka "Další" a "Předchozí" */
    private _previousAction: StandardAction;
    private _nextAction: StandardAction;

    /**True, pokud si detail vytvořil vlastní menu. V takovém případě ho pak i disposuje*/
    protected _isMenuOwner: boolean = false;
    public readonly canUseLikeDetail: boolean = false;
    public readonly isContainer = true;
    protected _canDelete: boolean | undefined | null = undefined;
    private _formCanInsert: boolean | undefined | null;
    /**
     * Mapa komponent nad jednotlivými atributy
     */
    private _attributesControlsMap: Map<string, IAttributeControl[]> = new Map<string, IAttributeControl[]>();
    public container: HTMLElement;
    public _actualObject: OWObject;
    public readonly uuid: string;

    public error: any;
    public refreshObjectAfterSetActive: boolean = false;
    public refreshListAfterSetActive: boolean = false;
    private __actualObjectStandardRights: StandardOperationRights;
    private _decryptItemAction: StandardAction;
    private _classEncryptionAllowed: boolean = false;
    public isChanged: boolean = false;
    private _hideButtons: HideButtons;
    private _pendingTasks: Array<Promise<void>> = [];
    private _componentTree: ComponentTreeType;
    protected __rightsManager: RightsManagerType;
    protected __rightOverrides: DetailFrameRightsOverrides;
    protected _interactive: boolean = true;
    /** Drží si hodnotu o tom, zda se na detail frame aplikují formulářová práva. */
    protected __usesFormRights: boolean;
    /** Určuje, zda je povoleno zobrazení akce tisk záznamu */
    protected _allowReport: boolean;
    protected override _masterFrame: MasterableFrame;
    public frameName: string;

    public onEditMethod: string;
    public onCancelMethod: string;

    private _masterLinkToAttr: string = null;
    public get masterLinkToAttr(): string {
        return this._masterLinkToAttr;
    }

    protected _actualObjectSetDefferred: DeferredStateful<boolean> = PromiseUtils.deferStateful<boolean>();

    public keyboardSelectionChangeInitiated: boolean = false;
    public editModeAfterObjectChanged: boolean = false;

    private _masterFrameName: string;
    public get masterFrameName(): string {
        return this._masterFrameName;
    }
    public set masterFrameName(value: string) {
        this._masterFrameName = value;
    }

    private _isMainFrame: boolean = false;

    public get isMainFrame(): boolean {
        return this._isMainFrame;
    }

    public set isMainFrame(v: boolean) {
        this._isMainFrame = v;
    }

    public get renderStandardRowActions(): boolean {
        return true;
    }

    protected _formCanRead: boolean | undefined = undefined;
    public get formCanRead(): boolean {
        return this._formCanRead;
    }
    public set formCanRead(val: boolean) {
        this._formCanRead = val;
    }
    public get isMultiSelecting(): boolean {
        return this.masterFrame?.isMultiSelecting;
    }
    public get canEnterMultiEdit(): boolean {
        return this.masterFrame?.supportsMultiEditing;
    }
    public get isMultiEditing(): boolean {
        return (this.masterFrame as IMultiRecordsComponent)?.isMultiEditing;
    }

    public get isMultiSelectingMode(): boolean {
        return this.isMultiSelecting;
    }

    /** Informace, zda se pracuje s verzovanými daty */
    public get readVersioned(): boolean {
        return this.ownerForm?.readVersioned;
    }

    protected _isEditing: boolean = false; // pokud je detail frame v editace je true (property z duvodu nastavovani readonly na elemnty)

    //#region Events
    public readonly editModeChanged: Event<IEditableFrame, boolean> = new Event<IEditableFrame, boolean>();
    /**
     * Událost vyvolaná při uvolnění framu
     */
    private readonly _disposed: Event<DetailFrame, undefined> = new Event<DetailFrame, undefined>();
    protected _rightsEvaluatedWhenNotInteractive: Set<RightType>;

    private _canValueLoadChanged: (s: IAttributeControl | IControl, v: boolean) => Promise<void>;
    public frameIsVisible: Event<IFrame, any> = new Event<IFrame, any>();
    public detailResized: Event<any, any> = new Event<any, any>();
    public actualObjectChanged: Event<IFrame, IActualObjectChangedEventArgs> = new Event<IFrame, IActualObjectChangedEventArgs>();
    public actualObjectValueChanged: Event<IFrame, OWObject> = new Event<IFrame, OWObject>();
    public actualObjectSaved: Event<IMasterableFrame, OWObject> = new Event<IMasterableFrame, OWObject>();
    public changesCanceled: Event<IFrame, void> = new Event<IFrame, void>();
    public editingRow: Event<IFrame, OWObject> = new Event<IFrame, OWObject>();
    public readonly selectionModeChanged: Event<IMasterableFrame, boolean> = new Event<IMasterableFrame, boolean>();
    public readonly contextActionsReady: Event<IMasterableFrame, string> = new Event<IMasterableFrame, string>();
    public readonly initialized: StatefulEvent<IFrame, void> = new StatefulEvent<IFrame, void>();

    /**
     * Událost vyvolaná při otevírání kontextového menu ve framu
     */
    public readonly contextMenuOpening: Event<IFrame, IFrameCtxMenuData> = new Event<IFrame, IFrameCtxMenuData>();
    //#endregion Events

    private _detailControls: IAttributeControl[];
    public showOnlyOutside: boolean;
    private _firstOpenInEdit = true; // znaci prvni otevreni v editaci (pro vyhodnocovani prav na editaci)
    protected _isInitialized: boolean = false;
    protected _initializationStarted: boolean = false;
    protected _suppressSaveDialog: boolean = false;
    /**
     * Příznak, zda bylo framem iniciováno zavírání formuláře. Slouží k optimalizaci vyhodnocování
     * určitých práv, která se při zavírání formuláře vyhodnocovat nemusí. Může být zrušeno.
     */
    protected isFrameClosing: boolean = false;

    /**Pole pouzivane pro nacteni prav ze serveru*/
    private _classItemsRights: Array<IClassItem>;

    //toolbar
    public menu: IMenu;

    //#region Zatim neimplementovane prvky rozhrani
    public deleteRecord(): Promise<boolean> {
        throw new Error("Method not implemented.");
    }

    public override get enabled(): boolean {
        return this._enabled;
    }

    public override set enabled(value: boolean) {
        this._enabled = value;
        this._updateEnabledStatusForWorkflowActions();
    }

    public get isActive(): boolean {
        return this.enabled && this.interactive;
    }

    public get suppressSaveDialog(): boolean {
        return this._suppressSaveDialog || this.masterFrame?.suppressSaveDialog;
    }

    public get hideButtons(): HideButtons {
        if (!this._hideButtons)
            this._hideButtons = FrameSharedMethods.parseHideButtons(this.ownerForm, this.element.getAttribute("data-hidebuttons"), (this.ownerForm && this.ownerForm.visibleOnlyDetail));

        return this._hideButtons;
    }

    /**
     * Příznak, zda před vykonáním metody musí zkontrolovat doporučené atributy.
     */
    public readonly validateRecommended: boolean;

    public masterObject: OWObject = null;
    public readonly masterAttr: string = null;
    public hideToolBar: boolean = false;
    //#endregion Zatim neimplementovane prvky rozhrani

    /**
     * Chain promisů s probihajicimi updaty dat
     */
    private _waitForUpdatesTask: Promise<void> = Promise.resolve();

    //property pro resizovani detailu:
    /** Hodnota souradnice osy Y nebo osy X (podle aktualniho rozlozeni stranky) v momente, kdy se zaclo resizovat */
    private _initAxis: number = 0;
    /** Pocatecni vyska nebo sirka (podle aktualniho rozlozeni stranky) komponenty, kdyz se zaclo resizovat */
    private _initLen: number = 0;
    /** Property znacici, ze se zrovna komponenta resizuje */
    private _isResizing: boolean = false;
    /** Pocet pixelu od okraje, ve kterych komponenta reaguje na zacatek resizovani */
    private _pixelRes: number = 7;

    protected _registerFormUiControl: IRegisterFormUIControl[] = [];
    protected _validatableFrames: IValidatableFrame[] = [];
    /** Seznam atributů a jejich hodnot, které mají být zobrazeny v hlavičce formu/modálu */
    private _captionAttrs: Map<string, string>;

    // Aktivní role uživatele
    private _roleIdCollection: Array<string>;
    /** Udržuje informace o použitých atributech a detailových komponentách, ve kterých jsou zobrazeny */
    protected _attrInfoLoad: { [key: string]: IEditableControl[] } = {};
    //DOM preveden do stromove stuktury - startOfDOMStructure je Detail Frame a soucasne vychozim prvkem
    public startOfDomStructure: IDomStructureElement;
    //seznam podminek pouzitych na detailu

    private __validationManager: ValidationManager;

    protected _wfActions: Set<IWFAction> = new Set<IWFAction>();

    private _forceInEditStandartActionsState: boolean | undefined = undefined;
    protected _rightsUpdateCTS?: CancellationTokenSource;
    protected _detailCTS?: CancellationTokenSource = new CancellationTokenSource();
    protected _detailDataLoadCTS?: CancellationTokenSource = new CancellationTokenSource();
    protected _detailValueChangedCTS?: CancellationTokenSource = new CancellationTokenSource();
    //#endregion Fields

    //#region Listeners
    private _workflowItemsChangingListener: IListener;
    /** Listener na změnu editovatelnosti z formu */
    private _ownerReadOnlyChangedListener: IListener;

    private _masterFrameObjectChangedSetToEditHandler: (masterableFrame: IMasterableFrame, objectChangedEventArgs: IActualObjectChangedEventArgs) => void;
    //#endregion Listeners

    //#region Properties
    public get canDelete(): boolean | undefined | null {
        return this._canDelete;
    }

    public get canInsert(): boolean | undefined | null {
        return this._formCanInsert;
    }

    public set canInsert(value: boolean) {
        this._formCanInsert = value;
    }

    public get initializationStarted(): boolean {
        return this._initializationStarted;
    }

    public get isInitialized(): boolean {
        return this._isInitialized;
    }

    protected get _validationManager(): ValidationManager {
        if (this.__validationManager == null) {
            if (this.masterFrame && this.masterFrame.validationManager)
                this.__validationManager = this.masterFrame.validationManager;
            else
                this.__validationManager = new ValidationManager();
        }
        return this.__validationManager;
    }

    public get isValid(): boolean {
        return this.container ? !this.container.hasAttribute("invalid") : !this.element.hasAttribute("invalid");
    }

    public set isValid(v: boolean) {
        this.container ? ElementUtils.toggleAttribute(this.container, "invalid", !v) : ElementUtils.toggleAttribute(this.element, "invalid", !v);
    }

    public get isEditing(): boolean {
        return this._isEditing;
    }

    public set isEditing(val: boolean) {
        this.setDetailToEditMode(val, false);
    }

    public get interactive(): boolean {
        return this._interactive;
    }

    public set interactive(value: boolean) {
        this._interactive = value;
        this._updateEnabledStatusForWorkflowActions();
    }

    public get hasSplitter(): boolean {
        return !this.element.classList.contains("withoutSplitter");
    }

    public set hasSplitter(v: boolean) {
        this.element.classList.toggle("withoutSplitter", !v || GenericControl.hasParent(this.element, "[data-behaviour=container]")); // v kontejneru se o změnu jeho rozměrů stará kontejner
    }

    public get componentTree(): ComponentTreeType {
        return this._componentTree;
    }

    public set componentTree(componentTree: ComponentTreeType) {
        this._componentTree = componentTree;
    }

    // TODO-rites: je potřeba??
    public get mastersSuperComponentTree(): ComponentTreeType | null {
        if (!this.masterFrame)
            return null;

        return this.masterFrame.componentTree;
    }

    public get rightsManager(): RightsManagerType {
        if (!this._rightsManager)
            throw new InvalidOperationException("[DF] Rights hasn't been initialized yet!");
        return this._rightsManager;
    }

    protected get _actualObjectStandardRights(): StandardOperationRights {
        //TODO-rites: #pokud mám masterFrame, tak brát z něj?
        if (this._shouldUseRightsObjectFromMasterFrame()) {
            return this.masterFrame.actualObjectStandardRights;
        }
        if (this.__actualObjectStandardRights == null) {
            //throw new InvalidOperationException("Rights hasn't been loaded yet!");
            //logger.warn("Rights hasn't been loaded yet!"); // TODO-rites: #nemělo by nastat. Asi nechat tu vyjímku; c2: asi nakonec ne, bude se dotazovat defaultní hodnota na rightsManageru

            this._actualObjectStandardRights = FrameSharedMethods.getDefaultIEDRights(this.ownerForm, this._roleIdCollection);
        }
        return this.__actualObjectStandardRights;
    }

    protected set _actualObjectStandardRights(v: StandardOperationRights) {
        this.__actualObjectStandardRights = v;
    }

    public get actualObjectStandardRights(): StandardOperationRights {
        return this._actualObjectStandardRights;
    }

    /**
     * Formulářová práva by se měla aplikovat, pokud je frame hlavním framem nebo pokud je to detail k hlavnímu seznamu
     */
    protected get _usesFormRights(): boolean {
        if (this.__usesFormRights !== undefined) {
            return this.__usesFormRights;
        }

        let masterFrame: IMasterableFrame = this.masterFrame;
        while (masterFrame) {
            if (masterFrame.isMainFrame) {
                return this.__usesFormRights = true;
            }
            masterFrame = masterFrame.masterFrame;
        }
        return this.__usesFormRights = false;
    }

    public override get masterFrame(): MasterableFrame | undefined {
        return this._masterFrame;
    }

    public override set masterFrame(masterFrame: MasterableFrame | undefined) {
        if (this.element == null) // disposed
            return;

        if (this.masterFrame) { // Na tuto odregistraci spoléhá i dispose
            this.masterFrame.workflowItemsChanging.remove(this._workflowItemsChangingListener);
            this.masterFrame.actualObjectChanged.remove(this._masterFrameObjectChangedSetToEditHandler);
        }

        this._masterFrame = masterFrame;

        if (this.masterFrame == null || this.masterFrame.selectionChanged == null) {
            return;
        }

        this._events.add(this.masterFrame.selectionModeChanged, (sender, prm) => this.selectionModeChanged.trigger(sender, prm));

        if (this._isImplicit() && this.ownerForm.detailFormObject) {
            this.objectName = this.ownerForm.detailFormObject.objectName;
        } else if (masterFrame.objectName && !this._insertObjectName) { // Pokud je nastaven _insertObjectName, tak se nastavil do this.objectName v konstrutoru. Pak už nechceme znovu nastavovat objectName podle masterFramu
            this.objectName = masterFrame.objectName;
        }

        this._initializeMasterListeners();

        if (masterFrame instanceof IExtendedLifeCycle) {
            if (this.onEditMethod != undefined && masterFrame.onEditMethod == undefined) {
                masterFrame.onEditMethod = this.onEditMethod;
            } else {
                if (masterFrame.onEditMethod != undefined && this.onEditMethod == undefined) {
                    this.onEditMethod = masterFrame.onEditMethod;
                }
            }
            if (this.onCancelMethod != undefined && masterFrame.onCancelMethod == undefined) {
                masterFrame.onCancelMethod = this.onCancelMethod;
            } else {
                if (masterFrame.onCancelMethod != undefined && this.onCancelMethod == undefined) {
                    this.onCancelMethod = masterFrame.onCancelMethod;
                }
            }
        }

        this._masterLinkToAttr = this.masterFrame.masterLinkToAttr;
    }

    public override get ownerForm(): IForm {
        return super.ownerForm;
    }

    /**
     * Nastaveni ownerFormu - tj. Formu na kterem lezi MasterableFrame
     */
    public override set ownerForm(ownerForm: IForm) {
        super.ownerForm = ownerForm;
        if (this.ownerForm != null)
            this.ownerForm.readOnlyChanged.add(this._ownerReadOnlyChangedListener = (_sender: IForm, readOnly: boolean) => {
                this.interactive = !readOnly;
                this._evaluateInteractiveRights();
            });
    }

    public get actualObject(): OWObject {
        return this._actualObject;
    }

    public set actualObject(actualObject: OWObject) {
        this._actualObject = actualObject;
        if (actualObject != null) {
            // Dříve se volalo jen při prvním setu objektu, ale bude lepší volat při každém setu pro případy,
            // kdy je nadřízený frame nad rodičovskou třídou a záznamy jsou instance různých jejích potomků
            this._initializeComponentDataType();
        }

        if (this._actualObject != null) {
            this.isValid = this._actualObject.isValid;
        } else
            this.isValid = true;
    }

    public get hasSelectedRecord(): boolean {
        return this.actualObject != null;
    }
    public get isSetActualObjectRunning(): boolean {
        return !this._actualObjectSetDefferred.isResolved;
    }

    /**
     * Používá se pro ponechání akcí Uložit a Zrušit změny aktivních při editaci na esMulti ListFramu a použitém šifrování
     */
    public get forceInEditStandartActionsState(): boolean | undefined {
        return this._forceInEditStandartActionsState;
    }

    public set forceInEditStandartActionsState(value: boolean | undefined) {
        this._forceInEditStandartActionsState = value;
    }

    protected get _rightsManager(): RightsManagerType {
        if (!this.__rightsManager)
            this.__rightsManager = DIContainer.instance.create<RightsManagerType>(IRightsManager, this, this._createOrGetRightsOverrides());

        return this.__rightsManager;
    }

    protected get _insertObjectName(): string | null {
        return this.ownerForm.insertObjectName;
    }

    protected get _hasEditAction(): boolean {
        return !!this.menu?.getAction(ActionType.standardEditRecord)?.enabled;
    }

    /**
     * Prochází strom komponent a hledá aktivní editable controly, které mají povinnost na úrovni frm
     * @returns Mapu názvů povinných atributů s odpovídajícím id podmínky povinnosti, nebo s 0 pokud podmínka není
     */
    public get requiredAttrs(): Map<string, number> {
        const result = new Map<string, number>();
        const rightName = RightType[RightType.required] as keyof IItemRightsCollection;

        this._componentTree.traverseRootsFinishFirstAsync(async (el) => {
            const control = el.control;
            if (control instanceof IEditableControl && control.visible && control.enabled) {
                const rightCollection = ComponentRightsExtractor.loadRightsForTypes(control, RightType.required);
                const right = rightCollection[rightName] ? (rightCollection[rightName] as IItemRights[])[0] : null;
                const id = right?.conditionID;
                if (right?.level === RightLevel.formDefinitionLevel && (control.required || id)) {
                    result.set(control.attrName, id ? parseInt(id) : 0);
                }
            }
        });

        return result;
    }

    //#endregion Properties

    constructor(params: FormControlParams) {
        super(params);
        this._evaluateRightsOnAttributeChanged = debounce(this.__evaluateRightsOnAttributeChanged, 100, { leading: false, trailing: true });

        const { element, ownerForm } = params;

        this.visible = true;
        /**
         * V konstruktoru framu může být jen jednoduchá inicializace hodnot (z definice a pod.)
         * Logika a asynchronní procesy musí být až v inicializační metodě (initialize()),
         * která je vyvolávaná v potřebném pořadí a dá se na její doběhnutí čekat
         */

        this.element.classList.add("DetailFrame");

        this.frameName = this.element.getAttribute("data-framename");
        this.showOnlyOutside = this.getBoolDataAttr("ShowOnlyOutside");
        this.hideToolBar = this.getBoolDataAttr("hidetoolbar");
        this.masterFrameName = this.element.getAttribute("data-masterframe");
        this.locKey = this.element.getAttribute("data-captionlockey");
        this.validateRecommended = this.getBoolDataAttr("validaterecommended");
        this.onEditMethod = this.element.getAttribute("data-oneditmethod");
        this.onCancelMethod = this.element.getAttribute("data-oncancelmethod");
        this._suppressSaveDialog = this.getBoolDataAttr("suppresssavedialog", false);
        this._allowReport = this.getBoolDataAttr("printmenu", true);

        this._detailControls = new Array<IAttributeControl>();

        this._classItemsRights = new Array<IClassItem>();
        this._captionAttrs = new Map<string, string>();

        this.container = element.querySelector("[data-tsccontrol=AlignPanel]") as HTMLElement;

        this.ownerForm = ownerForm;

        this.objectName = this.ownerForm.detailFormObject?.objectName;
        if (this.ownerForm.openMode === OpenModeType.insert && this._insertObjectName)
            this.objectName = this._insertObjectName;

        const captionAttrs = this.getDataAttr("captionAttrs");

        if (captionAttrs) {
            captionAttrs.split(";").forEach(item => this._captionAttrs.set(item, null));
        }

        this._actualObjectSetDefferred.resolve(false);
        //DetailFrame má implicitně UseMasterContext
        this.useMasterContext = true;
        this.uuid = FrameSharedMethods.generateUuid(!!AppContext.instance.configuration.developmentMode, this.frameName);
    }

    public async initialize(): Promise<void> {
        await FrameSharedMethods.runWhenFirstShown(this, () => this._initializeVisual());
    }

    /**
     * Znicializuje celý frame. Musí být zavoláno před jeho prvním použitím. Je možné spouštět asynchronní procesy.
     */
    protected async _initializeVisual(): Promise<void> {
        if (this._initializationStarted) // inicializace může proběhnout jen jednou
            return;

        this._initializationStarted = true;

        /**
         * V této metodě by neměl být žádný výkonný kód.
         * Mělo by zde být pouze volání metod inicializujících jednotlivé oblasti a takovém pořadí, jaké je obecně potřeba
         */

        // nastavení detailu podle formu/mastera
        await this._initializeDetail();
        // ziniciování komponent ležících na detailu
        await this._initializeControls();
        // navázání listenerů
        this._initializeListeners();
        // vytvoření menu
        await this._initializeFormUI();
        // inicializace práv - vytvoření stromu a RightsManageru
        await this._initializeRights();

        this._isInitialized = true;
        this.initialized.trigger(this, undefined);
    }

    /** Nastavení detailu podle formu/mastera */
    protected async _initializeDetail(): Promise<void> {
        // Prázdná metoda
    }

    protected async _initializeControls(): Promise<void> {
        if (!this.container)
            return;

        // umístěno před získání attributeElements protože dříve nema CustomControl možnost ovlivnit data- atributy
        await Registrator.initializeFormControls(this.ownerForm, this.element);

        let attributeElements = helper.toArray<IHTMLElementWithControl>(this.container.querySelectorAll("[data-attrname][data-behaviour='component'],[data-attrname]:not([data-tscControl='TwoListFrame'])"));
        let methodElements = helper.toArray<IHTMLElementWithControl>(this.container.querySelectorAll("[data-methname][data-behaviour='action'],[data-methname][data-behaviour='button']"));

        // Najde vnořené DetailFramy a všechny jeho komponenty odebere ze seznamu, aby se neregistrovali k tomuto (vnějšímu) DetailFramu
        const subDetailElements = helper.toArray<IHTMLElementWithControl>(this.container.querySelectorAll("[data-behaviour='frame'][data-behaviour-subtype='detail']"));
        if (subDetailElements.length > 0) {
            const removeAttributeElements = new Array<IHTMLElementWithControl>();
            const removeMethodElements = new Array<IHTMLElementWithControl>();
            subDetailElements.forEach(subDetailElement => {
                removeAttributeElements.push(...helper.toArray<IHTMLElementWithControl>(subDetailElement.querySelectorAll("[data-attrname][data-behaviour='component'],[data-attrname]:not([data-tscControl='TwoListFrame'])")));
                removeMethodElements.push(...helper.toArray<IHTMLElementWithControl>(subDetailElement.querySelectorAll("[data-methname][data-behaviour='action'],[data-methname][data-behaviour='button']")));
            });
            attributeElements = attributeElements.filter(element => !removeAttributeElements.includes(element));
            methodElements = methodElements.filter(element => !removeMethodElements.includes(element));
        }

        const reactControlConnectors = helper.toArray<IHTMLElementWithControl>(this.container.querySelectorAll("[data-react-component='connector']"));

        reactControlConnectors
            .filter(element => !element.hasAttribute("data-attrname"))
            .map(element => (element.tscControl as any as IAttributeControl))
            .filter(control => control instanceof IRequiresActualObject)
            .forEach(control => this._detailControls.push(control));

        // je potřeba najít ještě elementy s BackgroundAttr, které nejsou spojeny s konkrétním atributem
        const otherBackgroundAttrElements = helper.toArray<IHTMLElementWithControl>(this.container.querySelectorAll("[data-backgroundattr]:not([data-attrname])"));

        for (const attrElement of attributeElements) {
            if (!attrElement.id)
                attrElement.id = UIManager.instance.generateElementID();
        }

        /* filtrovani kvuli odstranenym editum z Align Panelu (veskere komponenty se inicializuji nezavisle na sobe,
           takze ac AP muze smazat podrizenou komponentu, ktera je v nem nespravne (dokovani verzus pozicovani),
           tak DetailFrame ma stale na tuto komponentu (resp. jeji element) platny odkaz)
        */
        await Promise.all(attributeElements.filter((e) => !!e.parentElement).map(e => this._initializeControl(e)));

        otherBackgroundAttrElements.forEach(e => this._initializeBackgroundAttrControl(e));

        for (const element of methodElements) {
            const methName = element.getAttribute("data-methname");
            if (methName)
                this._classItemsRights.push({ name: methName, type: ClassItemType.Method });
            else
                logger.warn(`Unspecified method name on method element with id ${element.id}!`); // `
        }
    }

    /**
     * Zaktualizuje aktivnost workflow akcí podle aktivnosti framu (readonly a povolení právy)
     */
    protected _updateEnabledStatusForWorkflowActions(): void {
        if (!this.menu)
            return;

        for (const wfAction of this._wfActions) {
            wfAction.enabledVisual = this.interactive;
            wfAction.enabled = this.interactive;
        }
    }

    protected async _initializeControl(element: IHTMLElementWithControl): Promise<void> {
        const attrName = element.getAttribute("data-attrname");

        if (!attrName) {
            return;
        }

        const control: IAttributeControl = element.tscControl as any;
        if (!control)
            return;

        // ObjectListEditu se v gui nedefinuje vazba na masterframe, takže se mu žádný nenaváže, ale je potřeba ho nastavit kvůli MasterLinkToAttr a MasterAttr
        if (control instanceof IObjectListEdit && control instanceof IMasterableFrame && !control.masterFrame)
            control.masterFrame = this instanceof IMasterableFrame ? this : this.masterFrame;

        const editControl = control as IEditableControl;
        if (this._attrInfoLoad[attrName] === undefined) {
            this._attrInfoLoad[attrName] = [];
        }
        this._attrInfoLoad[attrName].push(editControl);

        if (this.actualObject == null || this.masterFrame == null)
            editControl.enabled = false;

        if (editControl instanceof IRegisterFormUIControl)
            this._registerFormUiControl.push(editControl);
        if (editControl instanceof IFilterInfo) {
            editControl.filterPanel = this.ownerForm.filterPanel;
            editControl.formName = this.ownerForm.formName;
        }

        if (editControl.valueChanged !== undefined) //pokud je napr. DataLabel, tak tuto udalost nema
            this._events.add(editControl.valueChanged, (sender, value) => {
                this._pendingTasks.push(this._valueChange(sender, value));
            });

        if (editControl instanceof IValidatableFrame) {
            this._validatableFrames.push(editControl);
        }
        if (editControl instanceof IValidatable)
            this._events.add(editControl.validityChanged, (sender, validity) => this._componentValidityChanged(sender as IValidatable & IAttributeControl, validity));

        // Udalost pro prechod do editace na kliknuti na editovatelnou polozku
        if (editControl instanceof IEditableControl && editControl.clicked) {
            this._events.add(editControl.clicked, async (component, event) => {
                if (event && (event.cancelBubble || event.ctrlKey || event.metaKey)) // někdo zrušil probublávání, tak by se neměl vykonávat ani tento kód (např. od LOV); ctrl = zobrazení informací o komponentě
                    return;

                if (
                    !this.isEditing &&
                    this.actualObject != null &&
                    (this._actualObjectStandardRights.canEdit || this.masterFrame?.supportsMultiEditing) &&
                    this._hasEditAction &&
                    this.interactive
                ) {
                    if (this.keyboardSelectionChangeInitiated) {
                        this.editModeAfterObjectChanged = true;
                        return;
                    }
                    if (!this.isSetActualObjectRunning && !this.editModeAfterObjectChanged && (!this.masterFrame || this.masterFrame.rightsManager.runningEvaluationsCount === 0)) {
                        await this.editRecord();
                        if (component instanceof ISetValueAfterClickComponent && component.clickInitializator)
                            component.clickInitializator.click();
                        PromiseUtils.delay(150).then(() => component.focus());
                    }
                }
            });
        }

        // Pokud se změnil výběr aktuálního záznamu, předává správnou hodnotu kurzoru do editační komponenty.
        if (editControl instanceof ICursorValueAdjustable) {
            this._events.add(this.actualObjectChanged, (_sender: IFrame, e: IActualObjectChangedEventArgs) => {
                editControl.cursor = e.actualObject ? Cursor.pointer : Cursor.initial;
            });
        }

        // Přidá komponentu do mapy komponent jednotlivých atributů
        let attributeControlsArray = this._attributesControlsMap.get(attrName);
        if (!attributeControlsArray) {
            this._attributesControlsMap.set(attrName, []);
            attributeControlsArray = this._attributesControlsMap.get(attrName);
        }
        attributeControlsArray.push(control);

        control.attrName = attrName;

        this._detailControls.push(control);

        this._classItemsRights.push({ name: control.attrName, type: ClassItemType.Attribute });
    }

    /**
     * Inicializuje a přidá do *this._detailControls* control pro element, který nemá attrName a zároveň má backgroundAttr.
     * Jelikož tomuto elementu chybí attrName, nedošlo by později k přidání jeho contolu do *this._detailControls* a tím pádem by
     * se ani nenastavil actualObject, který control potřebuje právě kvůli vlastnosti backgroundAttr.
     */
    protected _initializeBackgroundAttrControl(element: IHTMLElementWithControl): void {
        const control: IAttributeControl = element.tscControl as any;
        if (!control)
            return;

        this._detailControls.push(control);
    }

    /**
     * Resolvne promise čekající na dokončení změny objektu
     * @param value Typ dokončení promise - úspěšně/neúspěšně
     */
    protected _resolveActualObjectSetDefferred(value: boolean): void {
        if (!this._actualObjectSetDefferred.isResolved)
            this._actualObjectSetDefferred.resolve(value);
    }

    /**
     * Ceka na refresh dat
     */
    public waitForDataLoad(): Promise<boolean> {
        if (!this.isInitialized || this._isDisposed)
            return Promise.resolve(false);

        if (!this.masterFrame)
            return Promise.resolve(true);

        return this.masterFrame.waitForDataLoad();
    }

    /**
     * Ceka na dobehnuti zmen dat
     */
    public async waitForPendingUpdates(): Promise<void> {
        if (!this.isInitialized || this._isDisposed)
            return;

        if (this.masterFrame) {
            await this.masterFrame.waitForPendingUpdates();
            return;
        }

        await this._waitForUpdatesTask;
    }

    protected async _evaluateInteractiveRights(): Promise<void> {
        //pokud frame nebyl interaktivní, místo skutečného vyhodnocení práv si ukládál, která práva bude potřeba znovu vyhodnotit, až se stane interaktivní.
        const pendingRightsGroups = this._rightsEvaluatedWhenNotInteractive ? Array.from(this._rightsEvaluatedWhenNotInteractive) : undefined;
        await this._newEvaluateRights([RightType.interactive], pendingRightsGroups);

        if (this.interactive)
            this._rightsEvaluatedWhenNotInteractive = undefined;
    }

    /**
     * Zaregistruje probihajici zmenu dat
     */
    protected _enqueuePendingUpdateTask(task: Promise<void>): Promise<void> {
        if (this.masterFrame)
            return this.masterFrame.enqueuePendingUpdateTask(task);
        return this._waitForUpdatesTask = this._waitForUpdatesTask.then(() => task);
    }

    /**
     * Do fronty s vyhodnocováním práv přidá další ukol, který se provede, až doběhnou úkoly předchozí
     * @param task Vrací promis, na který lze čekat
     */
    protected _enqueueRightsEvaluationTask(task: Promise<void>): Promise<void> { // TODO-rites: podívat se na místa volání této metody, kde se předává this._rightsManager.evaluateRights a zvážit vytvoření metody, která bude přijímat group typů práv a rovnou v sobě zavolá tuto enqueue metodu
        if (this.masterFrame) {
            return this.masterFrame.enqueueRightsEvaluationTask(task);
        }
        return this._rightsEvaluationChainPromise = this._rightsEvaluationChainPromise.then(() => task);
    }

    public async waitForMastersSelectedRecordChange(): Promise<boolean> {
        if (this.masterFrame)
            await this.masterFrame.waitForSelectedRecordChange();

        return Promise.resolve(true);
    }

    /**
     * Počká na dokončení probíhajícího nastavení ActualObjectu na všech nadřízených framech
     */
    public async waitForMastersActualObjectSet(): Promise<boolean> {
        if (this.masterFrame)
            await this.masterFrame.waitForActualObjectSet();

        return Promise.resolve(true);
    }

    /**
     * Počká na dokončení probíhajícího nastavení ActualObjectu na tomto framu
     */
    public async waitForActualObjectSet(): Promise<boolean> {
        return this._actualObjectSetDefferred.promise;
    }

    /** Doplní data z objektu do detailových komponent */
    protected async _loadDataForDetail(): Promise<void> {
        if (this._detailControls.length === 0 || this._isDisposed) { // Dochází k tomu, pokud je objekt nastaven dřív, než dojde k inicializaci detailu
            return;
        }

        // musí se vyhodnotit a zrušit multiselect na komponentách, než se jim nastaví hodnoty, aby si je nemuseli uchovávat bokem,
        // když by zrovna měly zobrazenou fake komponentu "více hodnot"
        await this._newEvaluateRights([RightType.multiEditing]);

        this.isValid = true;

        this._detailDataLoadCTS = new CancellationTokenSource();

        await Promise.all(this._detailControls.map((control) => this._setDetailDataToControl(control, this._detailDataLoadCTS.token)));

        await this._updateMissingCaptionAttrs();
        await this.waitForMastersActualObjectSet();

        // po načtení dat a nastavení do komponent je potřeba znovu projít existující validační chyby a nastavit je komponentám
        if (this.actualObject)
            this._validationManager.checkDataErrors(this.actualObject.id);

        if (this.ownerForm.multiEditMode) // pokud je otevren jen detail v multieditaci, je potreba jeste jedno nacist prava na multieditaci, aby doslo ke spravnemu prepnuti mixinu
            await this._newEvaluateRights([RightType.multiEditing]);
    }

    private async _loadDataOnControl(detailControl: IAttributeControl, ct?: CancellationToken): Promise<void> {
        if (detailControl.isValueLoaded || this.actualObject == null)
            return;

        let val: IAttrValue = null;
        try {
            if (detailControl instanceof ILoader)
                detailControl.showLoader();

            if (detailControl.attrName)
                val = await this.actualObject.getValue(detailControl.attrName);

            if (this.isDisposed || ct?.cancellationRequested)
                return;

            if (detailControl instanceof IRequiresActualObject)
                detailControl.actualObject = this.actualObject;

            // az se bude loader zpracovavat ve vsech komponentach tak podminku zrusit, zatim jen pro LOV
            if (!(detailControl instanceof LOVEdit) && detailControl instanceof ILoader) {
                detailControl.hideLoader();
            }
            if (detailControl instanceof LOVEdit)
                detailControl.ownerForm = this.ownerForm;
            if (detailControl instanceof IVersioned)
                detailControl.readVersioned = this.ownerForm.readVersioned;

            if (detailControl instanceof TextLOVEdit) {
                detailControl.ownerForm = this.ownerForm;
            }

            if (detailControl instanceof IHasEncryptionState)
                detailControl.isVisuallyEncrypted = val instanceof IEncAttrValue && val.isEncrypted;

            if (detailControl instanceof IAttributeControl)
                detailControl.setInitialControlValue(AttrValueReader.read(val));

            if (detailControl instanceof IValidatable) {
                detailControl.validate();
            }

            this._updateCaptionAttrs(detailControl);

            detailControl.isValueLoaded = true;
        } catch (e) {
            if (!(e instanceof CancelError))
                logger.warn(`Error during setting value '${val}' of attribute '${detailControl.attrName}' of class '${this.objectName}' to component '${detailControl.constructor.name}'`, e);
        } finally {
            if (!this.isDisposed && detailControl instanceof ILoader)
                detailControl.hideLoader();
        }
    }

    private async _setDetailDataToControl(detailControl: IAttributeControl, ct: CancellationToken): Promise<void> {
        if (detailControl == null || this._isDisposed || ct.cancellationRequested)
            return;
        if (detailControl.canValueLoad) {
            detailControl.isValueLoaded = false;
            await this._loadDataOnControl(detailControl, ct);
        } else {
            if (!detailControl.canValueLoadChanged.containsListener(this._canValueLoadChanged))
                this._events.add(detailControl.canValueLoadChanged, this._canValueLoadChanged);

            if (detailControl instanceof IRequiresClassName) {
                detailControl.className = await this.actualObject.getObjectAttributeClass(detailControl.attrName);
            }

            if (detailControl.valueChanged)
                detailControl.valueChanged.enabled = false;
            detailControl.isValueLoaded = false;
        }
    }

    private _clearActualObject(): void {
        this._detailDataLoadCTS?.cancel();
        this._detailValueChangedCTS?.cancel();

        for (const control of this._detailControls) {
            if (control instanceof IRequiresActualObject) {
                if (!(control instanceof IFrame) || control.isInitialized) // má smysl pouze pro framy, které jsou inicializované
                    control.actualObject = null;
            } else
                control.controlValue = null;

            if (control instanceof IEditableControl) {
                control.enabled = false;
            }
        }

        if (this.actualObject != null) {
            // kvuli potrebe pri odstranovani validacnich chyb z komponent je potreba vynulovat objekt az po zmene validnosti komponent (po nastaveni nove hodnoty)
            this._events.removeByKey(this.actualObject.instanceID);
            this.actualObject = null;
        }

        //this._setRightsOnStandartActions();
        //this._rightsManager.evaluateRights(this._rightsManager.getControlRightGroupTypes(ControlRightTypesGroup.selectionChanged), this.actualObject, this._usesFormRights);
        this._newEvaluateRightsGroup(ControlRightTypesGroup.selectionChanged);
        this._resolveActualObjectSetDefferred(true);

        if (this._previousAction)
            this._previousAction.enabled = false;

        if (this._nextAction)
            this._nextAction.enabled = false;
    }

    /**
     * Vytvoří klíč pro registraci listenerů pro konkrétní instanci objektu.
     * @param instance Instance objektu (OW nebo MEOW).
     * @returns Vytvořený klíč.
     */
    protected _getInstanceListenersKey(instance: IOWObject): string {
        return `instanceListeners_${instance.instanceID}`;
    }

    //provaze objekt s detailem
    protected _setActualObject(obj: OWObject, isCalledFromFormInit?: boolean): void {
        //logger.log("DetailFrame.setActualObject() - Master: " + (this.MasterFrame ? this.MasterFrame.FrameName : "none") + ": " + (obj == null ? "null" : obj.ObjectName + ": " + obj.ID));

        this._detailDataLoadCTS?.cancel();

        const previousActualObject = this.actualObject;
        if (previousActualObject) {
            this._events.removeByKey(this._getInstanceListenersKey(previousActualObject));
        }
        this.actualObject = obj;

        // je možné, že se objekt dočte až později
        if (this.actualObject != null) {
            if (!isCalledFromFormInit) {
                this._loadDataForDetail();
            }

            const listenersKey = this._getInstanceListenersKey(this.actualObject);

            this._events.addWithKey(listenersKey, this.actualObject.attributesChanged, (sender: OWObject, attributeNames: string[]) => {
                this._pendingTasks.push(this._attributesChangedEventMethod(sender, attributeNames));
            });

            this._events.addWithKey(listenersKey, this._actualObject.attributesChanged, async (_sender: OWObject, attributeNames: Array<string>) => {
                for (const attr of attributeNames) {
                    this._validationManager.removeDataErrors(_sender.id, attr);
                }
            });

            this._events.addWithKey(listenersKey, this.actualObject.objectDecrypted, async () => {
                if (this.masterFrame && this.masterFrame.isEditing) {
                    await this.setDetailToEditMode(true, false);
                }
            });

            this._events.addWithKey(listenersKey, this.actualObject.dataRefreshed, () => this._refreshDisplayedData());
        }

        if (this._classEncryptionAllowed) {
            this._decryptItemAction.enabled = this.actualObject != null && this.actualObject.isClientSideEncrypted && !this.actualObject.isEncryptionFinalized;
        }

        this.actualObjectChanged.trigger(this, { actualObject: obj, previousActualObject: previousActualObject });

        this._resolveActualObjectSetDefferred(true);

        if (this._previousAction)
            this._previousAction.enabled = this.masterFrame?.canBeEnabled(ActionType.standardPreviousRecord);

        if (this._nextAction)
            this._nextAction.enabled = this.masterFrame?.canBeEnabled(ActionType.standardNextRecord);
    }

    protected async _initializeComponentDataType(): Promise<void> {
        const attributeNames = Object.keys(this._attrInfoLoad);
        for (const attrName of attributeNames) {
            if (!this.actualObject)
                break;

            let attributeMetadata: AttributeDescription = null;
            try {
                attributeMetadata = await this.actualObject.getAttributeMetadata(attrName);
            } catch (e) {
                // kvůli nestandardním přetypovaním, výjimku o neexistujicich atributech ignorujeme
            }

            if (attributeMetadata == undefined)
                continue;

            for (const control of this._attrInfoLoad[attrName]) {
                control.dataType = attributeMetadata.type;
                if ((attributeMetadata as IOWTextAttributeDescription).size && control instanceof IMaxLength) {
                    //pokud není zadán maxLength nebo databázový max length je více omezující
                    if (!control.maxLength || (attributeMetadata as IOWTextAttributeDescription).size < control.maxLength)
                        control.maxLength = (attributeMetadata as IOWTextAttributeDescription).size;
                }
            }
        }
    }

    protected _attributesChangedEventMethod = async (sender: OWObject, attributeNames: string[]): Promise<void> => {
        await Promise.all(attributeNames.filter(attr =>
            !attributeNames.some(attr2 => attr.startsWith(attr2) && attr.length > attr2.length && attr[attr2.length] === "."))
            .map(attr =>
                this._onObjAttributeChanged(sender, attr)));

        this._rightsUpdateCTS = new CancellationTokenSource();
        if (sender) {
            const cts = this._rightsUpdateCTS; // closure
            this._events.add(sender.disposed, () => cts.cancel()); // zruší vyhodnocování práv při dispose
            this._events.add(this._disposed, () => cts.cancel());
        }

        if (this._isDisposed) {
            this._rightsUpdateCTS.cancel();
            return;
        }

        // Pokud má detail masterFrame, měl by vyhodnocení práv detailu při změně atributu iniciovat tento masterFrame
        if (!this.masterFrame) {
            await this._evaluateRightsOnAttributeChanged();
        }
    };
    /**
     * Metoda spouští vyhodnocení práv při změně atributu.
     * Měla by být debounced, v některých případech se můžou naráz změnit desítky atributů a nechceme spouštět vyhodnocení práv pro každý.
     */
    protected readonly _evaluateRightsOnAttributeChanged: () => Promise<void>;
    protected async __evaluateRightsOnAttributeChanged(): Promise<void> {
        if (!this.isFrameClosing) {
            await (this._newEvaluateRightsGroup(ControlRightTypesGroup.attributeChange).catch(
                PromiseUtils.cancellationCatch));
        }
    }

    public showError(e: any): void {
        ErrorHandler.instance.handleError(e);
    }

    /**
     * obsluhuje zmenu hodnoty atributu na objektu
     * @param sender
     * @param attributeName
     */
    private async _onObjAttributeChanged(sender: OWObject, attributeName: string): Promise<void> {
        //Najdu vsechny atributy nebo atributy ve vazbach, ktere používají zmeneny atribut
        const controlsToUpdateMap: Map<string, IAttributeControl[]> = new Map<string, IAttributeControl[]>();
        const changedAttributeNameUpper = attributeName.toUpperCase();
        let instanceAssociations: string[];
        // Atributy změněných objektů, které jsou ve vazbě
        const linkedObjectAttributes: string[] = [];

        // Funkce pro načtení nové hodnoty atributu
        const fLoadAttr = async (attributeKey: string, attributeName: string) => {
            const attrValue = await this.actualObject.getValue(attributeName);
            if (attrValue &&
                attrValue instanceof IOWObject &&
                attrValue.instanceID === sender.instanceID) { // Kontrola, zda jde opravdu o instanci ve vazbě
                controlsToUpdateMap.set(attributeKey, this._attributesControlsMap.get(attributeKey));
                linkedObjectAttributes.push(attributeKey);
            }
        };

        // Prochází mapu všech atributů (na detail framu) a jejich komponent
        for (const [attributeKey] of this._attributesControlsMap) {
            const attributeKeyUpper = ObjectUtils.removeTypeCasting(attributeKey.toUpperCase());
            // Pokud je to přímo atribut aktuálního objektu
            if ((attributeKeyUpper == changedAttributeNameUpper) || (attributeKeyUpper.startsWith(`${changedAttributeNameUpper}.`))) { // ` pokud se rovná nebo začíná s tečkou, kvůli vazebním atributům
                // Přidám komponenty do mapy k aktualizaci
                controlsToUpdateMap.set(attributeKey, this._attributesControlsMap.get(attributeKey));
            } else if (instanceAssociations !== undefined || this.actualObject) {
            // Jedná se o nějaký atribut v podřízených vazbách AO
            // taky řeší situace, kdy je objekt ve vazbě "znovupoužit" a vazba parent není nastavena. (viz. InstanceManager)
                if (instanceAssociations === undefined) {
                    // Prohledá this.actualObjekt a pokusí se najít asociace/vazby obsahující sender objekt. Pokud takové najde budou v poli názvy těchto asociací.
                    instanceAssociations = await this.actualObject.getLinkNamesForInstance(sender.instanceID);
                }

                for (const attr of instanceAssociations) {
                    if (attributeKeyUpper === `${attr}.${attributeName}`.toUpperCase()) { // ` Pokud byla notifikována změna objektu ve vazbě
                        await fLoadAttr(attributeKey, attr);
                    }
                }
            }
        }

        if (controlsToUpdateMap.size > 0) {
            // Získám hodnotu změněného atributu
            const value = await sender.getValue(attributeName);

            // mezi vyvoláním změny atributu uživatelem a získáním odpovědi od serveru mohlo dojít ke změně objektu - taková změna se nesmí zpracovávat
            if (this.actualObject !== sender && linkedObjectAttributes.length === 0)
                return;

            for (const [attributeKey, attributeControls] of controlsToUpdateMap) {
                let newVal = value;
                // Pokud jde o vazební atribut
                if (attributeKey.indexOf(".") > -1 && value instanceof OWObject) {
                    // Pokud je hodnota změněného atributu neprázdná a zároveň je počet změněných objektů ve vazbách je nulový nebo mezi nimi není aktuálně procházený
                    if (value != null && ((linkedObjectAttributes.length === 0) || (linkedObjectAttributes.indexOf(attributeKey) === -1))) {
                        // Načte a použije se pro další nastavování hodnoty hodnota z vazby
                        // ReSharper disable once TsNotResolved
                        newVal = await (AttrValueReader.read(value) as OWObject).getValue(attributeKey.substr(attributeKey.indexOf(".") + 1));
                    }
                }

                for (const attributeControl of attributeControls) {
                    if (newVal instanceof EncAttrValue) {
                        attributeControl.isVisuallyEncrypted = newVal.isEncrypted;
                    } else {
                        attributeControl.isVisuallyEncrypted = false;
                    }
                    attributeControl.controlValue = AttrValueReader.read(newVal);
                }
            }
        }

        // pokud uživatel změnil hodnotu v komponentě, tak se odstraňují existující validační chyby
        this._validationManager.removeDataErrors(sender.id, attributeName);
    }

    /**
     * Obsluhuje změnu hodnoty na komponentě
     */
    protected async _valueChange(sender: IAttributeControl, value: any): Promise<void> {
        if (!this.actualObject)
            return; // musí být vybrán řádek

        const oldValue: IAttrValue = await this.actualObject.getValue(sender.attrName);

        if (!this.actualObject.hasEncryptionPassword && await this.actualObject.isAttributeEncrypted(sender.attrName)) {
            const pass = await this.actualObject.getEncryptionKey();
            const result = pass ? true : await FrameSharedMethods.showEncryptDecryptDialog(this.actualObject, true);

            if (!result) {
                //dialog byl stornován, vrátí se zpět původní hodnota.
                this.actualObject.setValue(sender.attrName, oldValue);
                sender.controlValue = oldValue;
                return;
            }
        }

        if (value !== undefined) {
            // Create context only if value is not undefined
            await this.createContext();
        }
        this._detailValueChangedCTS = new CancellationTokenSource();

        this._enqueuePendingUpdateTask(this.actualObject.setValue(sender.attrName, sender.controlValue, this._detailValueChangedCTS.token).then(async changedObject => {
            // pokud aplikační logika vrátila změnu na předchozí hodnotu, tak se hodnota atributu objektu nezmění (a nevyvolá se attributeChanged),
            // ale přesto je potřeba vrátit hodnotu i v komponentě
            // např: atribut obsahuje A, uživatel vloží do editační komponenty B, apl. logika to změní zpátky na A, apl. objekt se nezměnil, ale je potřeba i do editační komponenty vrátit A
            sender.controlValue = await changedObject.getValue(sender.attrName);

            this._updateCaptionAttrs(sender);

            if (this.masterFrame) {
                this.masterFrame.triggerActualObjectValueChange(changedObject);
            }
        }).catch((exception) => {
            if (!(exception instanceof CancelError)) {
                if (exception instanceof EOWValidateException) {
                    this._handleSaveErrors([{ data: this.actualObject, errors: exception.validationErrors }]);
                } else {
                    ErrorHandler.instance.handleError(exception);
                }
            } else {
                // Po zahození změn záznamu se nepřepsala hodnota na původní
                // (35258 - pokud se přepsala hodnota a ještě před ztrátou focusu se kliklo na "Zahodit změny", tak v komponentě zůstala změněný hodnota)
                sender.controlValue = oldValue;
            }
        }));
    }

    /**
     * Aktualizuje hodnoty atributů, které mají být zobrazeny v hlavičce formu a aktualizuje ji
     * @param control Komponenta, ze které je prováděna aktualizace
     */
    private async _updateCaptionAttrs(control: IAttributeControl): Promise<void> {
        if (!this._captionAttrs.has(control.attrName)) {
            return;
        }

        if (control instanceof IBussyAwaitable) {
            await control.waitForPendingActions();
        }

        this._captionAttrs.set(control.attrName, control.displayedValue != null ? control.displayedValue.toString() : null);

        this._updateFormTitle();
    }

    /** Aktualizuje hodnoty atributů, které mají být zobrazeny v hlavičce formu a nejsou zobrazeny přímo na formuláři */
    private async _updateMissingCaptionAttrs(): Promise<void> {
        if (this._captionAttrs.size === 0 || !this.actualObject) {
            return;
        }

        const displayedAttrs = Object.keys(this._attrInfoLoad);
        const missingAttrs = Array.from(this._captionAttrs.keys()).filter(attrName => !displayedAttrs.includes(attrName));

        for (const attrName of missingAttrs) {
            const value = AttrValueReader.read(await this.actualObject.getValue(attrName));

            this._captionAttrs.set(attrName, value != null ? value.toString() : null);
        }

        this._updateFormTitle();
    }

    /** Aktualizuje nadpis v hlavičce formu */
    private _updateFormTitle(): void {
        if (!this._isDisposed && this.ownerForm.visibleOnlyDetail) {
            this.ownerForm.customTitle = Array.from(this._captionAttrs.values()).filter(value => value).join(" | "); // Ten filter odstraní prázdné hodnoty
        }
    }

    /**
     * Do stromove struktury patri jen TcsControl prvky. Tahle metoda hleda prvni tscControl pro dalsi uroven stromu
     */
    private _findTcsControlChildren(element: HTMLElement, listofTscControl: IGenericControl[]): void {
        for (let j = 0; j < element.childNodes.length; j++) {
            if (element.childNodes[j].nodeType === 1) {
                if ((element.childNodes[j] as IHTMLElementWithControl).tscControl)
                    listofTscControl.push((element.childNodes[j] as IHTMLElementWithControl).tscControl);
                else {
                    this._findTcsControlChildren(element.childNodes[j] as HTMLElement, listofTscControl);
                }
            }
        }
    }

    /**
     * Přidá zatím nepřidané podmínky z práv na prvku
     * @param aktControl aktálně zpracovávaný prvek
     * @param itemRightsCollection kolekce práv, ze které se berou podmínky
     */
    private _addUnusedConditionId(aktControl: Control, itemRightsCollection: IItemRightsCollection): void {
        const aktItemCondId = ComponentRightsExtractor.getAllRightsIDs(itemRightsCollection, this._roleIdCollection);
        const localConditionIdList = new Array<string>();
        for (let j = 0; j < aktItemCondId.length; j++) {
            localConditionIdList.push(aktItemCondId[j].toString());
        }

        //Vybere ze seznamu podminek na formulari jen ty podminky, ktere jsou pouzity na seznamu a jeho komponentach
        for (const condItem of this.ownerForm.conditionItems) {
            if (localConditionIdList.indexOf(condItem.id) > -1) {
                if ((aktControl instanceof IStaticControl && aktControl.isStatic)) {
                    condItem.isStatic = true; // jedná se o statickou
                }
            }
        }
    }

    /**
     * Metoda, ktera prevadi DOM do interni stromove struktury
     */
    private _createTreeForDom(aktControl: Control): IDomStructureElement {
        const itemRightsCollection = ComponentRightsExtractor.loadRightsForTypes(aktControl, RightType.editableComponentRights);

        const controls: Control[] = [];
        this._findTcsControlChildren(aktControl.element, controls);
        const nodeControls: IDomStructureElement[] = [];
        //pri vytvareni DOM me nezajima co lezi v komponentach MasterableFramu
        if (!(aktControl instanceof MasterableFrame)) {
            for (let i = 0; i < controls.length; i++) {
                if (controls[i] != undefined)
                    nodeControls.push(this._createTreeForDom(controls[i]));
            }
        }
        this._addUnusedConditionId(aktControl, itemRightsCollection);
        const aktDom: IDomStructureElement = { control: aktControl, childControls: nodeControls, itemRightsCollection: itemRightsCollection };
        if (aktControl instanceof IStaticControl && aktControl.isStatic) {
            aktDom.evaluateRightsWithoutContext = true;
        }
        return aktDom;
    }

    /**
     * TODO-rites 21550: celé odstranit, měl by se používat pouze nový logický strom komponent, zbytečně se dělá tato náročná operace dvakrát
     * Spousti vytvareni DOM na detailu
     */
    protected async _initializeDom(): Promise<any> {
        //return; // TODO-rites: nebudeme provádět, ale až nebude k ničemu potřeba startOfDomStructure - např. validace

        this._roleIdCollection = await OWManager.getUserRoleIDs();

        // najde všechny podmínky na elementech uvnitř detailu
        this.startOfDomStructure = this._createTreeForDom(this);
    }

    public async refreshData(manualRefresh: boolean): Promise<void> {
        if (this.masterFrame)
            await this.masterFrame.refreshData(manualRefresh);
        else
            await this.refreshActualObject();
    }

    /** Aktualizuje ActualObject s aktualnim objektem */
    public async refreshActualObject(): Promise<void> {
        if (this.actualObject == null) {
            return;
        }

        this._validationManager.clearErrors(this.actualObject.id);

        if (!(this.actualObject instanceof MEOWObject)) {
            await this.actualObject.refresh();
        }

        await this._refreshDisplayedData();
    }

    /** Zaktualizuje zobrazená data z aktuálního objektu */
    protected async _refreshDisplayedData(): Promise<void> {
        await this._loadDataForDetail();

        if (!this.isFrameClosing) {
            await (this._newEvaluateRightsGroup(ControlRightTypesGroup.selectionChanged).catch(
                PromiseUtils.cancellationCatch));
        }
    }

    /** Aktualizuje ActualObject na základě kliku na akci refreshe v toolbaru */
    protected async _refreshClick(): Promise<void> {
        await (this.masterFrame ?? this).refreshActualObject();
    }

    /**
     * Vyhodnocuje práva formuláře (editace, vložení, mazání)
     * @param _ct
     */
    protected async _actualizeRights(_ct?: CancellationToken): Promise<void> {
        await this._newEvaluateRightsGroup(ControlRightTypesGroup.IED); //TODO-rites: ?? chceme to tu, když to v podstatě spouští vyhodnocení nad nadřízeným framem? nebude stačit že si to nadřízený vyřídí sám? A tento DF by potom přebíral výsledek?
    }

    protected _prepareButtons(ownerOfActions: IFrame): void {
        const buttonElements = Array.from(this.element.querySelectorAll("[data-behaviour='button']")) as IHTMLElementWithControl[];
        for (const buttonElement of buttonElements) {
            const el = buttonElement.tscControl as XGButton;
            if (el) {
                el.owner = ownerOfActions;
                if (el instanceof IControlWithXGAction) {
                    el.xgAction.ownerForm = this.ownerForm;
                }
            }
        }
    }

    /**
     * Nastaveni akci pro menu
     * @param menu aktualni menu
     */
    protected async _initializeFormUI(): Promise<void> {
        //logger.log("DetailFrame.RegisterFormUI, Master: " + (this.MasterFrame ? this.MasterFrame.FrameName : "none"));
        if (this.menu)
            return;

        if (this.masterFrame)
            this._canDelete = this.masterFrame.canDelete;
        else {
            const firmlyDisabledRights = await FirmlyDisabledRights.get(this.objectName);
            this._canDelete = !firmlyDisabledRights.delete;
        }

        //pokud masterframe neni null a je to seznam, ktery lezi na stejnem formulari a je neviditelny, tak nastavim detailu vysku 100%;
        if (this.masterFrame != null && this.masterFrame instanceof IMasterableFrame && (this.masterFrame).ownerForm == this.ownerForm && !(this.masterFrame).visible) {
            this.element.style.height = "100%";
            this.element.style.flexGrow = "1";
        }

        //pokud je použit jen jeden (hlavní) toolbar (např. ui-ribbon) použije se toolbar mastera (místo vytvoření nového). Naopak v případě kdy každý frame má vlasní toolbar (ui-standard) je potřeba vytvořit nový pokud je master skrytý.
        if ((this.masterFrame?.menu.usesSingleToolbar || this.masterFrame?.visible) && !this.ownerForm.detailFormMasterFrame) {
            this.menu = this.masterFrame.menu;
            this._isMenuOwner = false;
        } else {
            //DetailFrame je otevren v modalnim okne a edituje se v nem podrizeny seznam
            //Musi si proto vytvorit vlastni toolbar, neni-li to zakazano pres hideToolBar
            if (!this.hideToolBar) {
                this.menu = this.ownerForm.formUI.createFrameMenu(this);
                this._isMenuOwner = true;
            }

            // pokud je otevřen v insert modu, pak je bezpředmětné nastavovat objekt z mastera nebo ownera
            if (this.ownerForm.openMode !== OpenModeType.insert) {
                // detailu se nastaví objekt z master framu
                if (this.masterFrame || this.ownerForm.detailFormObject && this.masterFrame.ownerForm !== this.ownerForm) {
                    this._events.add(this.rightsManager.rightsEvaluated, () => {
                        this._loadDataForDetail();
                    }, { oneTimeOnly: true });

                    if (this.ownerForm.detailFormObject && this.masterFrame.ownerForm !== this.ownerForm) {
                        this._setActualObject(this.ownerForm.detailFormObject, true);
                    } else {
                        this._setActualObject(this.masterFrame.actualObject, true);
                    }
                }
            }

            if (this.masterFrame != null && !this.hideToolBar) {
                if (!this.ownerForm.multiEditMode) {
                    if (!this.hideButtons.prev && this.masterFrame.detailForm != null) {
                        this._createPreviousAction();
                    }
                    if (!this.hideButtons.next && this.masterFrame.detailForm != null) {
                        this._createNextAction();
                    }
                }

                if (this.ownerForm?.visibleOnlyDetail) {
                    await this.createDecryptAction();
                }

                await this._initializeWorkflowActions(this.masterFrame.getWorkflowMenuItems(), this.masterFrame, this.ownerForm, this.masterFrame.readVersioned);
            }
        }

        this.createHideButton();

        if (!this.ownerForm.showDetail)
            this._minimizeDetail(true);

        //Soucasne se zde inicializuje prevedeni DOM do stromove struktury
        await this._initializeDom();

        //await this._actualizeRights(); // není objekt, na kterém by se vyhodnocovalo -> pouze vytvoří instanci s daty s defaultními právy //TODO-rites: old rites; c2: asi tu bude potřeba alespoň vytvořit nějakou defaultní hodnotu(y), s insertem minimálně; c3: nahradit voláním this._newEvaluateRightsGroup; c4: DF přebírá od masterFramu, SDF si řeší po svém

        //tady musim zaregistrovat udalost na zmenu ActualObjectu, protoze je to prvni misto, ktere ma spolecny pruchod pro vsechny varianty umisteni detailu.

        if (this.menu) {
            if ((this.ownerForm?.visibleOnlyDetail && (this.masterFrame.ownerForm !== this.ownerForm)) || !this.masterFrame?.visible) {
                this._createRefreshAction(this.hideButtons.refresh);
            }

            if (this._allowReport)
                this._createReportAction();

            this._createInsertAction(this.hideButtons.insert);

            this._createCopyAction(this.hideButtons.copy);

            this._createEditAction(this.hideButtons.edit);

            this._createDeleteAction(this.hideButtons.delete);

            await this._addDefaultActionToMenu(this.hideButtons);
        }

        await this._createCustomActions(this.masterFrame);
        this._prepareButtons(this.masterFrame ?? this);

        this._registerFormUIControls();

        if (this.ownerForm.visibleOnlyDetail)
            this.menu?.setVisibility(this.canValueLoad);
    }

    protected async _initializeWorkflowActions(wfItems: IWorkflowMenuItem[], masterFrame: IMasterableFrame, ownerForm: IForm, readVersioned: boolean = false): Promise<void> {
        if (readVersioned || this.hideButtons.workflow) {
            return;
        }

        this._wfActions = await FrameSharedMethods.createWorkflowItems(wfItems, masterFrame, ownerForm) as Set<IWFAction>;
        this._wfActions.forEach((a) => this.menu.addAction(a));
    }

    /** Zaregistruje vytvořené detailové komponenty do formu */
    protected _registerFormUIControls(): void {
        for (const item of this._registerFormUiControl) {
            item.registerControlFormUI(this.ownerForm);
        }
    }

    protected async _initializeRights(additionalRightGroup?: ControlRightTypesGroup): Promise<void> {
        this._actualObjectStandardRights = FrameSharedMethods.getDefaultIEDRights(this.ownerForm, this._roleIdCollection);

        if (!this.masterFrame || (this.componentTree && this.componentTree.isPreinitializedOnly) || !this.componentTree) {
            const buildTreeParams: IBuildComponentTreeParameters = { frameContext: this.frameContext, isPreinitialized: !!this.componentTree };
            buildTreeParams.rightsEvaluationOverrides = this._createOrGetRightsOverrides();
            this._componentTree = ComponentTree.buildComponentTree(this, buildTreeParams);

            //TODO-rites: ~pokud šlo o případ "!this.componentTree" - DetailFrame je na detailFormu, tak musíme dodatečně pověsit strom tohoto DF na nadřízený MF, který je na předchozím framu. Stejně tak potom při zavření tento DF odpojit ze stromu nadřízeného MF; c2: o tohle se asi nakonec stará MF
        }

        this._rightsManager.setComponentTree(() => this._componentTree);

        let rightsToEvaluate = ControlRightTypesGroup.initialized | ControlRightTypesGroup.IED | additionalRightGroup;

        if (this.masterFrame == null || this.masterFrame.hasOpenedDetailInModal || this.masterFrame.isMultiSelectingMode || this._insertObjectName) {
            this._events.add(this.ownerForm.framesInitialized, async () => {
                let rightGroup = ControlRightTypesGroup.initialized;

                //Pokud detail frame používá actualObjectStandardRights z masterframe není potřeba v rámci inicializace vyhodnocovat skupinu IED práv.
                if (!this._shouldUseRightsObjectFromMasterFrame())
                    rightGroup = rightGroup | ControlRightTypesGroup.IED;

                rightsToEvaluate = rightGroup | rightsToEvaluate;
                await this._newEvaluateRightsGroup(rightsToEvaluate); // TODO-rites: ~nechat tady? asi jo; c2: spouštět až po inicializaci všech framů, jinak mohlo dojít k situaci, že se na LF iniciuje vyhodnocení práv a stromem projde na podřízený detail, který ještě není inicializován a chybí mu třeba rightsManager
                this._checkOpenInEditAndSetEditMode();
            });
        } else {
            // po #33828: Hromadná editace samotného seznamu. Se může detail otevírat v multiEditaci a je potřeba vyhodnotit i práva IED. (např. Po zahodit změny musí být vyhodnoceno, že může záznam zase rozeditovat)
            await this._newEvaluateRightsGroup(rightsToEvaluate);
        }
    }

    protected _createOrGetRightsOverrides(): DetailFrameRightsOverrides {
        if (!this.__rightOverrides)
            this.__rightOverrides = new DetailFrameRightsOverrides(this);

        return this.__rightOverrides;
    }

    protected async _newEvaluateRights(rightTypes: RightType[], priorityRightTypes?: RightType[]): Promise<void> {
        const rightsEvaluationWrappedPromise = (async () => {
            await this.waitForActualObjectSet();
            if (this._isDisposed) {
                // Může nastat situace, kdy dojde k dispose detailu ještě před vyhodnocením práv. V takovém případě už nelze práva vyhodnocovat, protože je disposed i rights manager.
                return Promise.resolve();
            } else {
                return this._rightsManager.evaluateRights(rightTypes, this.actualObject, this._usesFormRights, priorityRightTypes);
            }
        })();

        this.addRightsEvaluatedWhenNotInteractive(rightTypes);

        this._enqueueRightsEvaluationTask(rightsEvaluationWrappedPromise);

        return rightsEvaluationWrappedPromise;
    }

    /**
     * Pro neinteraktivní frame si uloží práva, aby je mohl vyhodnotit jakmile se stane interaktivní. Pozn. Nemá smysl vyhodnocovat práva pro neinteraktivní frame.
     * @param rightTypes - typy práv
     */
    public addRightsEvaluatedWhenNotInteractive(rightTypes: RightType[]): void {
        if (!this._interactive) {
            if (this._rightsEvaluatedWhenNotInteractive)
                this._rightsEvaluatedWhenNotInteractive = new Set([...Array.from(this._rightsEvaluatedWhenNotInteractive), ...rightTypes]);
            else
                this._rightsEvaluatedWhenNotInteractive = new Set(rightTypes);
        }
    }

    //TODO-rites: zrevidovat, s jakými typy práv se z jakých míst volá
    protected async _newEvaluateRightsGroup(rightGroup: ControlRightTypesGroup): Promise<void> {
        return this._newEvaluateRights(RightGroups.getControlRightGroupTypes(rightGroup), RightGroups.getControlPriorityRightGroupTypes(rightGroup));
    }

    /**
     * Vytvori akce definovane v XGenu (DetailFormAction, atd.)
     * @param master - masterframe
     */
    protected async _createCustomActions(master?: MasterableFrame): Promise<void> {
        const actionElements = Array.from(this.element.querySelectorAll("[data-behaviour='action']")) as IHTMLElementWithControl[]; //vytahne vsechny akce z definice formulare

        for (const actionElement of actionElements) {
            // přeskočení akcí z vnořených framů
            const parentFrame = GenericControl.findParent(actionElement, "[data-behaviour=frame]");
            if (parentFrame !== this.element)
                continue;

            let isNew: boolean = false;
            if (!actionElement.tscControl) {
                //pokud neni inicializovana, tak se ji pokusi znovu inicializovat a nakonec přidat pomocí _addActionToDom
                await Registrator.initializeControls(actionElement);
                isNew = true;
            }

            if (!actionElement.tscControl) //pokud i presto neni inicializovana, tak pokracuje na dalsi
                continue;

            const action = actionElement.tscControl; //vytahne objekt typu action z html elementu
            if (!(action instanceof XGAction))
                throw new InvalidOperationException(`Action id ${actionElement.id} is not an instance of XGAction`); //`

            if (master)
                action.owner = master;
            else
                action.owner = this;

            if (action instanceof XGAction) {
                action.ownerForm = this.ownerForm;
            }

            if (action instanceof MethodAction) {
                this._classItemsRights.push({ name: action.methodName, type: ClassItemType.Method });
            }

            if (this.menu)
                this.menu.addAction(action);

            if (isNew)
                this._addActionToDom(action, actionElement);
        }
        //MasterFrame je skrytý, proto jeho akce přidám do toolbaru detailFramu
        if (master && !master.visible) {
            for (const aktAction of master.actions) {
                if (aktAction.control instanceof XGAction) {
                    const action = aktAction.control;
                    action.owner = this;

                    //var locKye = _element.getAttribute('data-lockey');
                    // vsechny akce jsou defaultne enabled a nektere je potreba povypinat
                    if (action instanceof DetailFormAction) {
                        action.enabled = false;
                    } else if (action instanceof IStaticControl) {
                        if (!action.isStatic)
                            action.enabled = false;
                    } else if (!(action instanceof NodeAction)) {
                        action.enabled = false; // ostatni nevyjmenovane akce jsou defaultne zakazane
                    }

                    this.menu.addAction(action);

                    this._addActionToDom(action, action.element as IHTMLElementWithControl);
                }
            }
        }
    }

    /**
     * Pokud je povoleno, tak přidá do toolbaru akci pro refresh
     * @param hbRefresh Hodnota z hidebuttons
     */
    protected _createRefreshAction(hbRefresh: boolean): void {
        if (hbRefresh)
            return;

        const xgaction = FrameSharedMethods.createStandardAction(ActionType.standardRefreshRecord, () => this._refreshClick(), this);
        this.menu.addAction(xgaction);
    }

    /**
     * Pokud muze tak do toolbaru prida insertaction/newrecordaction
     * @param hbInsert - hodnoto z hidebuttons
     */
    protected _createInsertAction(hbInsert: boolean): void {
        if (hbInsert)
            return;

        if (!this.masterFrame) // záznamy se zakládají na masteru
            return;

        if (!this.objectName && !this.masterFrame?.objectName) // bez toho není jak poznat, jakou třídu se má zakládat
            return;

        const xgaction = FrameSharedMethods.createStandardAction(ActionType.standardNewRecord, async () => {
            await this.newRecord();
        }, this);

        xgaction.enabled = false;
        if (this.masterFrame && this.masterFrame instanceof IEditStyleMasterableFrame)
            xgaction.visible = !this.isEditing || this.masterFrame.editStyle == EditStyleType.esMulti;
        else
            xgaction.visible = !this.isEditing;
        this.menu.addAction(xgaction);
    }

    /**
     * Pokud muze tak do toolbaru prida copyaction
     * @param hbCopy - hodnoto z hidebuttons
     */
    protected _createCopyAction(hbCopy: boolean): void {
        if (hbCopy)
            return;

        if (!this.masterFrame) // záznamy se kopírují na masteru
            return;

        const xgaction = FrameSharedMethods.createStandardAction(ActionType.standardCopyRecord, () => this.createCopy(), this);

        xgaction.enabled = false;
        if (this.isEditing)
            xgaction.visible = false;
        this.menu.addAction(xgaction);
    }

    /**
     * Pokud muze tak do toolbaru prida editaction
     * @param hbEdit - hodnoto z hidebuttons
     */
    protected _createEditAction(hbEdit: boolean): void {
        if (hbEdit)
            return;

        const xgaction = FrameSharedMethods.createStandardAction(ActionType.standardEditRecord, async () => {
            await this.editRecord();
        }, this);

        if (this._actualObjectStandardRights.canEdit) {
            //TODO: MasterFrame je editovatelny, ale soucasne ma detail. Co s tim? kdo ma reagovat na klliknuti na editaci? seznam nebo detail. Silverlight odregistruje akci na listFramu a zaregistruje ji na detail
            xgaction.enabled = true;
        }
        if (this.isEditing)
            xgaction.visible = false;
        this.menu.addAction(xgaction);

        if (this.masterFrame?.supportsMultiEditing) {
            const action = FrameSharedMethods.createMultiSelectAction(ActionType.standardEditRecords, async () => {
                await this.setDetailToEditMode(true, true);
            }, this);
            this.menu.addAction(action);
        }
    }

    /**
     * Pokud muze tak do toolbaru prida editaction
     * @param hbDelete - hodnoto z hidebuttons
     */
    protected _createDeleteAction(hbDelete: boolean): void {
        if (hbDelete)
            return;

        if (!this.masterFrame) // záznamy se kopírují na masteru
            return;

        const xgaction = FrameSharedMethods.createStandardAction(ActionType.standardDeleteRecord, async () => {
            await this.masterFrame.deleteRecord(this.ownerForm.isDetailForm && this.masterFrame?.detailForm != null);
        }, this);

        xgaction.enabled = this._actualObjectStandardRights.canDelete && this.canDelete;

        if (this.isEditing)
            xgaction.visible = false;
        this.menu.addAction(xgaction);
    }

    /**
     * Přidá do toolbaru akci tisku
     */
    protected _createReportAction(): void {
        const action = FrameSharedMethods.createRecordReportAction(this, this._getRequestContext());

        if (action != null) {
            action.enabled = true;
            action.toMenu = MenuType.mtPrint;

            this.menu.addAction(action);
        }
    }

    /**
     * Pridavani akce do DOM, aby se nad ni mohli vyhodnotit prava
     */
    private _addActionToDom(action: XGAction, element: IHTMLElementWithControl, _ind: number = 0): void {
        const itemRightsCollection = ComponentRightsExtractor.loadRightsForTypes(element.tscControl, RightType.actionRights);
        const nodeControls: IDomStructureElement[] = [];
        const aktDom: IDomStructureElement = { control: action, childControls: nodeControls, itemRightsCollection: itemRightsCollection };
        this.startOfDomStructure.childControls.push(aktDom);
    }

    /**
     * Přidá do menu akce uložení a zrušení změn
     * @param hideButtons Určuje, zda mají být uvedená tlačítka přidána
     */
    protected async _addDefaultActionToMenu(hideButtons: HideButtons): Promise<void> {
        let action: StandardAction;
        if (!hideButtons.save) {
            if (await this.supportsEditContext && this.masterFrame?.hasHandedContext || this.masterFrame?.useMasterContext) {
                if ((action = FrameSharedMethods.createStandardAction(ActionType.standardSaveRecordInEditContext, async () => {
                    await this.saveAllClick();
                }, this)) != null)
                    this._createDefaultActionToMenu(action);
            } else {
                if ((action = FrameSharedMethods.createStandardAction(ActionType.standardSaveRecord, async () => {
                    await this.saveRecordClick();
                }, this)) != null)
                    this._createDefaultActionToMenu(action);

                if ((action = FrameSharedMethods.createStandardAction(ActionType.standardSaveAll, async () => {
                    await this.saveAllClick();
                }, this)) != null)
                    this._createDefaultActionToMenu(action);
            }
        }

        if (!hideButtons.cancel) {
            if ((action = FrameSharedMethods.createStandardAction(ActionType.standardCancelChanges, () => this.cancelChanges(this.ownerForm.isDetailForm && this.masterFrame?.detailForm != null), this)) != null) {
                this._createDefaultActionToMenu(action);
            }
        }
    }

    private _createDefaultActionToMenu(action: StandardAction): void {
        if (action == null) //akce nebyla definována v UI, nebude renderována
            return;
        if (!this.isEditing) {
            action.visible = false;
        }
        this.menu.addAction(action);
    }

    protected _handleSaveErrors(e: SaveResultData[]): void {
        for (const error of e) {
            const objectInstanceId = error?.data?.instanceID;

            if (error.errors == undefined)
                continue;

            for (const e of error.errors) {
                const componentsData: IValidatableComponentData[] = [];

                if (e.attributeNames != null) {
                    for (const attrName of e.attributeNames) {
                        const hasAttrDescription = this.actualObject?.classInfo?.attributes.find(desc => desc.attributeName === attrName);
                        let elements: NodeListOf<Element> | Element[];
                        try {
                            elements = this.element.querySelectorAll(`[data-attrname='${attrName}' i]`);
                        } catch (ex) { //callback pro prohlížeče nepodporující ignore case v selektoru
                            elements = Array.prototype.filter.call(this.element.querySelectorAll("[data-attrname]"), (elem: Element) => {
                                return StringUtils.compareCaseInsensitive(elem.getAttribute("data-attrname"), attrName);
                            });
                        }
                        for (const el of elements) {
                            const control = (el as IHTMLElementWithControl).tscControl as EditableControl;
                            if (!(control instanceof IEditableControl))
                                continue;

                            // prvni nalezeny kontejner - napr. Page nebo DetailFrame
                            // TODO: container zde nebude potreba hledat, az se vytvori logicky strom komponent a predany control bude nodem stromu,
                            // TODO: potom pujde automaticky projit vsechny jeho rodicovske kontejnery az kde to bude potreba (ve ValidationManageru)
                            const container = this._findComponentContainer(el);

                            if (!hasAttrDescription) {
                                ValidationManager.invalidateComponents([{ control: control, container: container, attributeName: attrName, showsInvalidData: () => true }], e); //pokud se jedná o chybu z atributu jiného objektu, než je aktuální, vyvolá pouze proces znevalidnění komponenty
                            } else {
                                componentsData.push({ control: control, container: container, attributeName: attrName, showsInvalidData: () => this.actualObject != null && this.actualObject.instanceID === objectInstanceId });
                            }
                        }
                    }
                }

                this._validationManager.addDataError(error.data as any as IValidationDataObject<IValidationData>, e, componentsData);
            }
        }
    }

    //#region ToolbarAction
    /**
     * Uloží konkrétní rozeditovaný záznam
     */
    public async saveRecordClick(): Promise<boolean> {
        try {
            this.masterFrame.autoRefresher?.pause();
            return await this.saveRecords(this.actualObject.id, this.validateRecommended) === null;
        } catch (e) {
            ErrorHandler.instance.handleError(e);
            return false;
        }
    }

    /**
     * Uloží všechny rozeditované záznamy
     */
    public async saveAllClick(): Promise<boolean> {
        try {
            this.masterFrame.autoRefresher?.pause();
            return await this.saveRecords(null, this.validateRecommended) === null;
        } catch (e) {
            ErrorHandler.instance.handleError(e);
            return false;
        }
    }

    /**
     * Navratove hodnoty:
     * null - ulozeni probehlo v poradku
     * prazdne pole - ulozeni se nepodarilo, ale neni co zpracovat
     * naplnene pole - vraci vyjimky, vznikle pri ukladani
     * @param id Pokud je vyplněn, uloží pouze jeden záznam. V opačném případě ukládá všechny rozeditované objekty.
     * @param checkRecommended Pokud je vyplněno, tak validuje i doporučená pole
     * @param checkPostponed Pokud je vyplněno, tak validuje i povinně odložená pole
     * @param skipValidation Pokud je true, tak klient nebude validovat pole
     * @param isFormClosing Příznak zdali se zavírá formulář
     * @param skipSavingEditContextId Příznak zdali se má ukládat editační kontext
     */
    public async saveRecords(id?: string, checkRecommended?: boolean, checkPostponed?: boolean, skipValidation?: boolean, isFormClosing?: boolean, skipSavingEditContextId?: boolean): Promise<SaveResultData[] | null> { //TODO [TP] - v dalsim kole kdy se bude pridavat postponeRequired by se to melo predelat na objekt
        const beforeSaveAll: Array<Promise<void>> = [];
        this.componentTree.traverseDFS((control) => {
            if (control.control instanceof IBeforeSaveAwaitable) {
                beforeSaveAll.push(control.control.beforeSave());
            }
        });

        await Promise.all(beforeSaveAll);

        // pred ukladanim je potreba pockat na mozne probihajici updaty
        await this.waitForPendingUpdates();
        await this.waitForPendingActions();

        // Pokud nepřijde seshora checkRecommended nastavené na true, použije nastavení atributu z detail framu
        const checkRecommendedLocal = checkRecommended ? checkRecommended : this.validateRecommended;

        if (this.actualObject) {
            // Před uložením promažu existující chyby
            this._validationManager.clearErrors(null, ValidationErrorType.postponedError);

            this._addValidationErrosForLoadedAttrs();
            await this.actualObject.waitForUpdateInstance();
        }

        let rightsGroup = ControlRightTypesGroup.beforeSave;
        if (!this._shouldUseRightsObjectFromMasterFrame()) {
            rightsGroup = rightsGroup | ControlRightTypesGroup.IED;
        }

        await this._newEvaluateRightsGroup(rightsGroup);

        const saveResult = await this.masterFrame.saveRecords(id, checkRecommendedLocal, checkPostponed, skipValidation, isFormClosing, skipSavingEditContextId);

        if (saveResult === null || saveResult?.length === 0) {
            await this.saveContext();

            if (this.masterFrame.hasOpenedDetailInModal && this.isEditing) {
                // Nemůže být voláno z listFramu, pokud je detail otevřen v novém okně, tak listFrame nemá referenci na tento detail.
                await this.setDetailToEditMode(this.masterFrame?.isEditContextInitiator ?? false, this.interactive);
            }
        } else {
            this._handleSaveErrors(saveResult);
        }

        return saveResult;
    }

    /**
     * Zruší změny provedené na aktuálně vybraném záznamu. V případě, že se jedná o nový záznam, dojde k jeho zahození.
     * @param maintainSelection - Pokud došlo k zahození záznamu, zkusí označit záznam na pozici místo něj nebo nad ním.
     */
    public async cancelChanges(maintainSelection: boolean = false): Promise<void> {
        this._detailCTS.cancel();
        await this.unlockObject();
        await this.masterFrame?.cancelChanges(maintainSelection);
        await this.masterFrame?.rightsEvaluationTask;
        await this.setDetailToEditMode(false, this.interactive);
        await this.rollbackContext();

        this._detailCTS = new CancellationTokenSource();
        // pokud ma master frame jineho ownera, jedna se o detail v novem okne a pri zruseni zmen se zavre
        //if (this.OwnerForm != this.MasterFrame.OwnerForm) this.CloseModalWindow();
    }

    public async closeModalWindow(): Promise<boolean> {
        return this.ownerForm.close();
    }

    public async editRecord(): Promise<boolean> {
        if (this.actualObject) {
            if (!await this.lockObject(this.actualObject))
                return false;

            //nastaví flag, že je objekt rozeditován. Narozdíl od isChanged, ale nemusí mít změněnou žádnou hodnotu.
            this.editingRow.trigger(this, this.actualObject);
        }

        //kontrola jestli zobrazit dialog pro uložení změn při pokusu o rozeditování dalšího záznamu.
        if (this.isEditing && this.masterFrame && this.masterFrame instanceof IEditStyleMasterableFrame)
            return this.ownerForm.checkAndSaveForm({ sourceFrame: !(this.masterFrame.editStyle === EditStyleType.esMulti) ? null : this.masterFrame, forceSave: this._forceAutoSave, skipSavingEditContextId: this.useMasterContext });

        // zkontroluje se uložení změn na masteru a jeho nadřízených framech
        if (!(await this.ownerForm.checkAndSaveForm({ forceSave: this._forceAutoSave, skipSavingEditContextId: this.useMasterContext })))
            return false;

        await this.setDetailToEditMode(true, true);
        // nastavení viditelnosti a práv na standardní akce musí být až po změně stavu editace!
        //this._setRightsOnStandartActions();
        //        await this._rightsManager.evaluateRights(this._rightsManager.getControlRightGroupTypes(ControlRightTypesGroup.inEdit), this.actualObject, this._usesFormRights).promise;
        return true;
    }

    public async newRecord(objectName?: string): Promise<boolean> {
        // zkontroluje se uložení změn na masteru a jeho nadřízených framech
        if (this.ownerForm.isDetailForm && !(await this.ownerForm.checkAndSaveForm({ forceSave: this.suppressSaveDialog })))
            return false;

        const initialFill = this.masterFrame.afterGuiCreateFill;

        // Pokud zakládáme objekt na param. formuláři otevřeném bez seznamu (skrz ShowInsertAction, nebo DetailFormAction s ShowOnlyDetail="True"), masterem se stane LF, na kterém ležely Actiony.
        // To je problém, ze schovaného LF nemůžeme získat AGCF, řeší se tak, že Actioně nastavíme požadovaný AGCF také, ta ho předá otevíranému formu, a zde se dočasně přiřadí masterFramu.
        if (this.ownerForm !== this.masterFrame.ownerForm) {
            this.masterFrame.afterGuiCreateFill = `${this.masterFrame.afterGuiCreateFill ?? ""}${this.ownerForm.setValues ?? ""}`;
        }

        const masterSuccess = await this.masterFrame.newRecord(objectName || this._insertObjectName || this.objectName);

        // Vrátíme masterFram do původního stavu, nastavení otevíraného frm/actiony (Pro většinu useCase budou tyto 2 stejné) tam zůstat nemůže
        this.masterFrame.afterGuiCreateFill = initialFill;

        if (masterSuccess)
            await this.setDetailToEditMode(true, true);

        return masterSuccess;
    }

    /**
     * Vytvoří kopii záznamu
     */
    public async createCopy(): Promise<void> {
        try {
            if (!(await this.masterFrame.createCopy(true))) {
                return;
            } else
                await this.ownerForm.setToolbarToEditMode(true);
        } catch (exception) {
            if (exception instanceof EOWValidateException) {
                this._handleSaveErrors([{ data: this.actualObject, errors: exception.validationErrors }]);
            } else {
                ErrorHandler.instance.handleError(exception);
            }
        }
    }

    public prevRecord(): void {
        this.masterFrame.prevRow();
    }

    public nextRecord(): void {
        this.masterFrame.nextRow();
    }

    //#endregion ToolbarAction

    private _hideDetailButton: HTMLElement;
    // @ts-ignore // nečteno-ponecháno
    private _isMinimized = false;

    private _minimizeDetail(minimize: boolean): void {
        if (minimize) {
            this._isMinimized = true;
            this._hideDetailButton.classList.add("minimalized");
            this.element.classList.add("minimalized");
            Localization.addLocDescData(this._hideDetailButton, "GUI-668940", "title");//maximalizovat detail
        } else {
            this._hideDetailButton.classList.remove("minimalized");
            this.element.classList.remove("minimalized");
            this._isMinimized = false;
            Localization.addLocDescData(this._hideDetailButton, "GUI-668939", "title");//minimalizovat detail
        }
        this.detailResized.trigger(null, null);
    }

    public createHideButton(): void {
        this._hideDetailButton = document.createElement("div");
        if (!this.ownerForm.visibleOnlyDetail)
            this._hideDetailButton.classList.add("hide-detail-button", "icon", "ui-triangle-bottom", "btn");
        this.element.appendChild(this._hideDetailButton);
        Localization.addLocDescData(this._hideDetailButton, "GUI-668939", "title");//minimalizovat detail

        this._events.addListener(this._hideDetailButton, "click", () => {
            if (this._hideDetailButton.classList.contains("Disabled")) {
                return;
            }

            this._minimizeDetail(!this._isMinimized);

            if (this._isMinimized) {
                this.detailResized.trigger(null, null); // Slouží k dočtení dat do seznamu po minimalizaci detailu
            }
        });
    }

    /**
     * Nastaveni viditelnosti tlacitka pro minimalizaci detailu
     * @param visibility
     */
    private _hideButtonVisibility(visibility: boolean): void {
        if (visibility) {
            this._hideDetailButton.classList.add("btn-show");
            this._hideDetailButton.classList.remove("btn-hidden");
        } else {
            this._hideDetailButton.classList.add("btn-hidden");
            this._hideDetailButton.classList.remove("btn-show");
        }
    }

    protected _createPreviousAction(): void {
        this._previousAction = FrameSharedMethods.createStandardAction(ActionType.standardPreviousRecord, async () => {
            if (this.ownerForm.isDetailForm && await this.ownerForm.checkAndSaveForm({ forceSave: this._forceAutoSave, skipSavingEditContextId: this.useMasterContext }))
                this.masterFrame.prevRow();
        }, this);
        this._previousAction.enabled = this.masterFrame?.canBeEnabled(ActionType.standardPreviousRecord);
        this.menu.addAction(this._previousAction);
    }

    protected _createNextAction(): void {
        this._nextAction = FrameSharedMethods.createStandardAction(ActionType.standardNextRecord, async () => {
            if (this.ownerForm.isDetailForm && await this.ownerForm.checkAndSaveForm({ forceSave: this._forceAutoSave, skipSavingEditContextId: this.useMasterContext }))
                this.masterFrame.nextRow();
        }, this);
        this._nextAction.enabled = this.masterFrame?.canBeEnabled(ActionType.standardNextRecord);
        this.menu.addAction(this._nextAction);
    }

    protected get _forceAutoSave(): boolean {
        return (this.masterFrame?.useMasterContext || this.masterFrame?.hasHandedContext || this.suppressSaveDialog);
    }

    /**
     * Vytvori tlacitko pro desifrovani
     */
    public async createDecryptAction(): Promise<void> {
        const classInfo = await ModelDescriptionsManager.instance.getClassInfo(this.objectName);
        this._classEncryptionAllowed = classInfo.encryptingAllowed;
        if (!classInfo.encryptingAllowed)
            return;

        this._decryptItemAction = FrameSharedMethods.createDecryptToolbarAction(() => this.decryptObject(), this);
        this.menu.addAction(this._decryptItemAction);

        this._decryptItemAction.enabled = this.actualObject != null && this.actualObject.isClientSideEncrypted && !this.actualObject.isEncryptionFinalized;
    }

    public async decryptObject(): Promise<void> {
        if (!this.actualObject)
            return;
        if (this.actualObject.isServerSideEncrypted) {
            if (!PasswordManager.hasPassword(this.actualObject.objectName, this.actualObject.id)) {
                //EncAttrValue
                const result = await FrameSharedMethods.showEncryptDecryptDialog(this.actualObject, false);
                this._decryptItemAction.enabled = !result;
            }
        }
    }

    /**
     * Nastaveni eventu
     */
    protected _initializeListeners(): void {
        const debouncedRights = debounce(() => this._newEvaluateRightsGroup(ControlRightTypesGroup.initialized), 300, { trailing: true });
        this._canValueLoadChanged = async (s: IAttributeControl, v: boolean) => {
            if (!v)
                return;

            const detailControl: IAttributeControl = s;

            if (detailControl instanceof IFrame && !detailControl.isInitialized)
                detailControl.initialized.add(() => this._loadDataOnControl(detailControl), true);
            else
                await this._loadDataOnControl(detailControl);

            // po nacteni dat je treba zavolat opetovne vyhodnoceni prav, protoze nektere komponenty mohou dostat doplnujici informace
            // jako treba LOV nazev tridy, ktery se pouziva pro vyhodnocovani prav
            debouncedRights();

            ///Aktivace disabled události valueChanged. K deaktivaci dochází v metodě _setDetailDataToControl.
            if (detailControl.valueChanged && !detailControl.valueChanged.enabled)
                detailControl.valueChanged.enabled = true;

            if (this.actualObject == null)
                return;

            // po zobrazeni skryte zalozky se vyvola tato udalost a do komponent na drive skryte zalozce se nastavit data
            // soucasti toho si komponenta bezne udela vlastni validaci (a tim zahodi pripadne serverove validacni chyby)
            // proto je potreba znovu nastavit pripadne nevalidnosti
            if (detailControl instanceof IValidatable)
                this._validationManager.checkComponentErrors(this.actualObject.id, detailControl);
        };

        this._events.addListener(this.element, "mousedown", (e: MouseEvent) => this._onMouseDown(e));

        if (this.element.parentElement)
            this._events.addListener(this.element.parentElement, "mousemove", (e: MouseEvent) => this._onMouseMove(e));

        this._events.addListener(this.element, "mouseup", (e: MouseEvent) => this._onMouseUp(e));

        this._events.addListener(window, "resize", () => {
            this.detailResized.trigger(null, null);
            window.dispatchEvent(new CustomEvent("resizecomponent"));
        });

        // zpracovani udalosti zmeny validnosti objektu
        this._events.add(this._validationManager.objectValidityChange, (_s, val) => {
            if (this.actualObject != null && this.actualObject.id == val.object.id) {
                this.isValid = val.validityState;
            }
        });

        this._events.addListener(this.element, "contextmenu", (e: MouseEvent) => {
            this.contextMenuOpening.trigger(this, { dataKey: (this.actualObject && this.actualObject.id) || null, originalEvent: e });
        });

        // OpenMode insert/edit když je zobrazený pouze detail. Je třeba kromě this.ownerForm.visibleOnlyDetail vyhodnotit i viditelnost master frame.
        if ((this.ownerForm.openMode === OpenModeType.insert || this.ownerForm.openMode === OpenModeType.edit) && (this.ownerForm.visibleOnlyDetail || !this.masterFrame.visible)) {
            this._events.add(this.initialized, async () => {
                if (this.ownerForm.openMode === OpenModeType.insert && this.masterFrame)
                    await this.newRecord(this._insertObjectName);
                //TODO-RITES: bude ještě potřeba? Při přepnutí DF do editace se kontroluje jestli je sám v modalu a visible přidá do vyhodnocení.
                await this._newEvaluateRights([RightType.visible, ...RightGroups.getControlRightGroupTypes(ControlRightTypesGroup.inEdit)]);
            });
        }

        this._events.add(this.actualObjectChanged, async (_sender: IFrame) => {
            this.keyboardSelectionChangeInitiated = false;
            if (this.ownerForm.isDetailForm && FrameSharedMethods.isFormNameImplicit(this.masterFrame?.detailForm) && !this.actualObject.isNew
            && (await FrameSharedMethods.getDefaultFormName(
                "IMPLICIT",
                this.masterFrame.ownerForm,
                this.actualObject,
                this.frameContext,
            )).FormName !== this.ownerForm.formName) {
                const mf = this.masterFrame;
                await this.ownerForm.close();
                mf.editRecord();
            }
        });

        this._events.add(this.ownerForm.stateChanged, async (_sender, state) => {
            if (state.state === WindowState.realVisibility && this.masterFrame?.isEditContextInitiator && !this.isEditing)
                await this.setDetailToEditMode(true, true);
        });

        if (this.masterFrame)
            this._events.add(this.masterFrame.contextActionsReady, () => this._checkOpenInEditAndSetEditMode());
    }

    /** Inicializuje listenery mastera */
    protected _initializeMasterListeners(): void {
        const masterFrame = this.masterFrame;

        if (masterFrame) {
            this._events.add(masterFrame.actualObjectChanged, async (sender: IMasterableFrame, args: IActualObjectChangedEventArgs) => {
                //logger.log("masterFrame.actualObjectChanged", this.frameName, e.actualObject);
                const validateEmptyComponents = !sender || sender.actualObject !== null; //Pokud se na masteru nezměnil objekt na null, kontrola se neprovede.

                if (this._actualObjectSetDefferred.isResolved) {
                    this._actualObjectSetDefferred = PromiseUtils.deferStateful<boolean>();
                }

                if (this.isEditing && validateEmptyComponents && args?.actualObject?.instanceID !== args?.previousActualObject?.instanceID) {
                    await this.checkEmptyComponents(this.validateRecommended);
                }

                await masterFrame.waitForActualObjectSet();

                if (masterFrame.actualObject == null) {
                    this._clearActualObject();
                } else if (this.masterFrame.ownerForm !== this.ownerForm && this.masterFrame instanceof IMasterableFrameWithDataGrid && this.masterFrame.formObjectName) {
                    const detailObject = (await masterFrame.actualObject.getValue(this.masterFrame.formObjectName));
                    if (detailObject instanceof OWObject) {
                        this._setActualObject(detailObject);
                    }
                } else {
                    this._setActualObject(masterFrame.actualObject);
                }
            });
        }

        masterFrame.workflowItemsChanging.add(this._workflowItemsChangingListener = async (masterableFrame: IMasterableFrame, workflowMenuItems: IWorkflowMenuItem[]) => {
            // aktualizovat wf položky v toobaru, jen pokud má masterFrame a detail odlišný toolbar (detail otevřen modálně - ikona (i)); pokud by byly stejné, zajstí načtení už masterFrame
            if (this.menu && this.menu !== masterFrame.menu) {
                FrameSharedMethods.clearWorkflowItems(this._wfActions, this.menu);

                await this._initializeWorkflowActions(workflowMenuItems, masterableFrame, this.ownerForm);
            }
        });

        if (masterFrame instanceof IMasterableFrameWithDataGrid) {
            this._events.add(masterFrame.dataGridInitialized, () => {
                if (masterFrame instanceof IMasterableFrameWithDataGrid) {
                    masterFrame.currentDataGrid.autoSelectFirstRecord = true;
                }
            });
            if (masterFrame instanceof IFrameWithKeyboardNavigation) {
                this._events.add(masterFrame.keyboardSelectionChangeInitiated, () => {
                    this.keyboardSelectionChangeInitiated = true;
                });
            }
        }

        if (masterFrame instanceof IEditableFrame || masterFrame instanceof ITwoListFrame) {
            this._events.add(masterFrame.editModeChanged, async (_sender, value) => await this.setDetailToEditMode(value, false));
        }

        if (masterFrame) {
            this._events.add(masterFrame.rightsManager.rightsEvaluated, () => this.rightsManager.rightsEvaluated.trigger(this.rightsManager, this.actualObject));
        }
    }

    /** Vrací objekt, který obsahuje informace o prvku */
    public async getControlInfo(childInfo: Partial<IControlInfoObject>, clickedControl: IGenericControl): Promise<Partial<IControlInfoObject>> {
        return FrameSharedMethods.getControlInfo(this, childInfo, clickedControl);
    }

    /**
     * Metoda pro event mousedown
     */
    private _onMouseDown(e: MouseEvent): void {
        //var cachedSel = $(this.Element);
        if (this.element.style.height == "100%" || this._isMinimized)
            return;

        const formLay: boolean = this.ownerForm.horizontalLayout;

        let aRel: number = 0; //vzdalenost od okraje detailu, kam bylo kliknuto

        const rect = this.element.getBoundingClientRect();

        if (formLay == true)
            aRel = e.pageX - rect.left;
        else
            aRel = e.pageY - rect.top;

        if (aRel < this._pixelRes) { //pokud je podminka splnena bylo kliknuto do oblasti, ktera je urcena pro resize detailu
            if (formLay == true) {
                this._initLen = this.element.clientWidth;
                this._initAxis = e.pageX;
            } else {
                this._initLen = this.element.clientHeight;
                this._initAxis = e.pageY;
            }
            this._isResizing = true;
        }
    }

    public setDetailLayout(): void {
        if (this.element.firstElementChild.scrollHeight > this.element.firstElementChild.clientHeight) {
            const fits = this.ownerForm.element.getElementsByClassName("FormInternalPanel")[0];
            fits.classList.remove("fits");
        } else {
            const fits = this.ownerForm.element.getElementsByClassName("FormInternalPanel")[0];
            fits.classList.add("fits");
        }
    }

    /**
     * Metoda pro event mousemove
     */
    private _onMouseMove(e: MouseEvent): void {
        if (!this.hasSplitter)
            return;

        const formLay: boolean = this.ownerForm.horizontalLayout;

        const splitFnc = (aRel: number, orinetation: string) => {
            this.element.style.cursor = orinetation;
            //-5 znamena jako moc mimo okraj detailu se zobrazi tlacitko pro minimalizaci
            if (aRel > -5)
                this._hideButtonVisibility(true);
            else
                this._hideButtonVisibility(false);
        };
        if (this._isResizing) {
            if (e.buttons == 0) {
                this._onMouseUp(e);
                return;
            }
            if (formLay == true) {
                this.element.style.width = this._initLen - (e.pageX - this._initAxis) + "px";
                //this.DetailResized.trigger(null, null);
            } else
                this.element.style.height = this._initLen - (e.pageY - this._initAxis) + "px";

            this.detailResized.trigger(null, null);
            window.dispatchEvent(new CustomEvent("resizecomponent"));
        } else {
            if (formLay == true) {
                const aRel = e.pageX - $(this.element).offset().left;
                if (aRel < this._pixelRes)
                    splitFnc(aRel, "w-resize");
                else {
                    this.element.style.cursor = "auto";
                    this._hideButtonVisibility(false);
                }
            } else {
                const aRel = e.pageY - $(this.element).offset().top;
                if (aRel < this._pixelRes)
                    splitFnc(aRel, "n-resize");
                else {
                    this.element.style.cursor = "auto";
                    this._hideButtonVisibility(false);
                }
            }
        }
    }

    /**
      * Metoda pro event mouseup
      */
    private _onMouseUp(_e: MouseEvent): void {
        if (this._isResizing == true) {
            this._isResizing = false;
            this.element.style.cursor = "auto";
            UIManager.instance.windowResized.trigger(this, undefined);
            //Form.CheckGrids();
        }
    }

    public async lockObject(object: OWObject): Promise<boolean> {
        if (this.onEditMethod && object) {
            try {
                await object.run({
                    methodName: this.onEditMethod,
                    requestContext: this._getRequestContext(),
                });
            } catch (err) {
                ErrorHandler.instance.handleError(err);
                return false;
            }
        }
        return true;
    }

    public async unlockObject(object: OWObject = this.actualObject): Promise<boolean> {
        if (this.onCancelMethod && !(this.masterFrame instanceof IExtendedLifeCycle) && !this.actualObject?.isNew) { // pokud je IExtendedLifeCycle, pak v cancelChanges provede onCancelMethod
            try {
                await object?.run({
                    methodName: this.onCancelMethod,
                    requestContext: this._getRequestContext(),
                });
            } catch (err) {
                ErrorHandler.instance.handleError(err);
                return false;
            }
        }
        return true;
    }

    protected get isDisposed(): boolean {
        return this._isDisposed;
    }

    //destruktor
    public override dispose(): void {
        if (this._isDisposed)
            return;

        this.rollbackContext();

        if (this.masterFrame) {
            this.masterFrame.actualObjectChanged.remove(this._masterFrameObjectChangedSetToEditHandler);
        }

        this.unlockObject();

        this.actualObject = null;

        const controls = Array.prototype.slice.call(this.element.querySelectorAll("[data-tsccontrol]")) as IHTMLElementWithControl[];

        for (const element of controls) {
            if (GenericControl.hasParent(element, parent => parent.matches("[data-behaviour='form']") && parent !== this.ownerForm.element) || element.matches("[data-behaviour='form']")) {
                continue;
            }

            const control = element.tscControl;
            if (control instanceof IDisposable)
                control.dispose();

            element.tscControl = null;
        }

        this.ownerForm?.readOnlyChanged?.remove(this._ownerReadOnlyChangedListener);

        if (this._detailControls)
            this._detailControls.length = 0;
        if (this._classItemsRights)
            this._classItemsRights.length = 0;
        this._attributesControlsMap?.clear();

        //menu se disposne, jen pokud ho nevlastní jiný frame.
        if (this.menu && this._isMenuOwner) {
            this.menu.dispose();
        }

        this.initialized.removeAll();
        this.contextMenuOpening.removeAll();
        this.frameIsVisible.removeAll();
        this.detailResized.removeAll();
        this.actualObjectChanged.removeAll();
        this.rightsManager.dispose();
        this.actualObjectValueChanged.removeAll();
        this.changesCanceled.removeAll();
        this.editingRow.removeAll();

        super.dispose();
        this._disposed.trigger(this, undefined);
        this._disposed.removeAll();
    }

    /**
     * Umožňuje čekat na běžící procesy na komponentě a jejích podkomponentách
     */
    public async waitForPendingActions(): Promise<void> {
        if (!this.isInitialized || this._isDisposed)
            return;

        if (this._pendingTasks.length > 0) {
            await Promise.allSettled(this._pendingTasks);
        }

        this._pendingTasks = new Array<Promise<void>>();
        await this._waitOnDataLoaded(this.startOfDomStructure);
        this._waitForPendingTasksOnComponents(this.startOfDomStructure);
        await Promise.allSettled(this._pendingTasks);

        // když je _masterFrame editační LF je potřeba počkat na doběhnutí editace
        if (this._masterFrame instanceof IBussyAwaitable) {
            await this._masterFrame.waitForPendingActions();
        }
    }

    private async _waitOnDataLoaded(parent: IDomStructureElement): Promise<void> {
        if (parent == null)
            return;

        if (parent.childControls.length > 0 && parent.control.visible) {
            for (let i = 0; i < parent.childControls.length; i++) {
                await this._waitOnDataLoaded(parent.childControls[i]);
            }
            return;
        }

        const control = parent.control;
        if (control instanceof IDataLoadingAwaitable) {
            await control.waitOnDataLoaded();
        }
    }

    //TODO-rites: předělat na procházení ComponentTree
    private _waitForPendingTasksOnComponents(parent: IDomStructureElement): void {
        if (parent == null)
            return;

        // rekurzivni prochazeni potomku
        if (parent.childControls.length > 0 && parent.control.visible) {
            for (let i = 0; i < parent.childControls.length; i++) {
                this._waitForPendingTasksOnComponents(parent.childControls[i]);
            }
            return;
        }

        const control = parent.control;
        if (control === this)
            return;

        if (control instanceof IBussyAwaitable) {
            this._pendingTasks.push(control.waitForPendingActions());
        }
    }

    /**
     * Umožňuje zrušit běžící procesy na komponentě
     */
    public cancelPendingActions(): void {
        throw new Error("Not implemented");
    }

    //#region Prava
    /**
     * Kontroluje jestli jsou vyplnena povinna pole a pripadne chyby vklada standardnim zpusobem do ValidationManageru
     * @param checkRecommended jestli maji se kontrolovat doporucena pole
     * @param checkPostReq jestli se maji kontrolovat odlozene povinnosti
     * @param validatedUnchangedObjects jestli se ma kontrola provest i když není záznam změněný
     */
    public async checkEmptyComponents(checkRecommended?: boolean, checkPostReq?: boolean, validatedUnchangedObjects?: boolean): Promise<void> {
        if (this.element == null) // disposed
            return;

        if (this.actualObject != null && (this.actualObject.isChanged || this.actualObject.isNew || validatedUnchangedObjects)) // nezmenene zaznamy se neukladaji a nema cenu na nich delat validaci povinnosti
            await this._checkEmptyComponents(this.startOfDomStructure, false, checkRecommended, !this.isMultiEditing, checkPostReq);
    }

    private async _checkEmptyComponents(parent: IDomStructureElement, invalidContainerActivated = false, checkRecommended?: boolean, checkRequired: boolean = true, checkPostReq?: boolean): Promise<boolean> {
        if (this.element == null) // disposed
            return false;

        // rekurzivni prochazeni potomku
        if (parent.childControls.length > 0 && parent.control.visible) {
            for (let i = 0; i < parent.childControls.length; i++) {
                if (this.element == null) // disposed
                    return false;

                invalidContainerActivated = await this._checkEmptyComponents(parent.childControls[i], invalidContainerActivated, checkRecommended, checkRequired, checkPostReq);
            }
            return invalidContainerActivated;
        }

        const control = parent.control;
        if (control instanceof IEditableControl) {
            if (!control.attrName) {
                logger.warn(`Unspecified attribute name on component ${control.constructor.name} id ${control.element.id}!`); // `
                return invalidContainerActivated;
            }

            // pokud ještě nebyla do komponenty načtena hodnota (např. je na skryté záložce), tak ji tam načteme teč, jelikož je nutná pro provedení validace (která musí proběhnout i na skrytých záložkách)
            if (!control.isValueLoaded) {
                if (control instanceof IRequiresActualObject) {
                    control.actualObject = this.actualObject;
                }
                // je žádoucí zjišťovat hodnotu jen v případě, že se na komponentě vyhodnocuje validnost
                if (control.required || control.recommended || control.postponedRequired) {
                    control.setInitialControlValue(await this.actualObject.getValue(control.attrName));
                    control.isValueLoaded = true;
                }
            }

            // na nevalidni komponente nema cenu kontrolovat vyplneni povinnych poli
            if (!control.isValid)
                return invalidContainerActivated;

            if (checkRequired && !control.isFilledRequiredControl()) {
                //Pokud je prvni nevyplnene povinne pole na jine zalozce nez je ta aktivni tak ji vyberu
                if (!invalidContainerActivated) {
                    const element = GenericControl.findParent(control.element, (el) => el.classList.contains("Page")) as IHTMLElementWithControl;
                    if (element && element.tscControl instanceof Page) {
                        element.tscControl.tab.pageTabClick();
                        invalidContainerActivated = true;
                    }
                }

                control.isInRequiredInvalidState = true;

                // closure
                const objectId = this.actualObject.id;

                this._validationManager.addComponentError(this.actualObject as any as IValidationDataObject<IValidationData>, {
                    type: ValidationErrorType.requiredError,
                    errorMessage: await Localization.getTextPromise("GUI-55627"), // "Nevalidní nebo nevyplněné povinné pole"
                    componentNames: [control.componentName],
                }, {
                    control: control,
                    attributeName: control.attrName,
                    container: this._findComponentContainer(control.element),
                    showsInvalidData: () => this.actualObject.id === objectId,
                });
            }
            if (checkRecommended && !control.isFilledRecommendedControl()) {
                const objectId = this.actualObject.id;
                this._validationManager.addComponentError(this.actualObject as any as IValidationDataObject<IValidationData>, {
                    type: ValidationErrorType.recommendedError,
                    errorMessage: await Localization.getTextPromise("GUI-643083"), // "Nevyplněné doporučené hodnoty"
                    componentNames: [control.componentName],
                }, {
                    control: control,
                    attributeName: control.attrName,
                    container: this._findComponentContainer(control.element),
                    showsInvalidData: () => this.actualObject.id === objectId,
                });
            }
            if (checkPostReq && !control.isFilledPostponedControl()) {
                control.isInRequiredInvalidState = true;
                const objectId = this.actualObject.id;
                this._validationManager.addComponentError(this.actualObject as any as IValidationDataObject<IValidationData>, {
                    type: ValidationErrorType.postponedError,
                    errorMessage: await Localization.getTextPromise("GUI-55627"), //"Nevalidní nebo nevyplněné povinné pole"
                    componentNames: [control.componentName],
                }, {
                    control: control,
                    attributeName: control.attrName,
                    container: this._findComponentContainer(control.element),
                    showsInvalidData: () => this.actualObject.id === objectId,
                });
            }
        }

        return invalidContainerActivated;
    }

    /**
     * Projde všechny komponenty a najde ty které jsou nevalidní. U těchto pak nastaví znovu validitu, tak jako by hodnoty změnil uživatel.
     * Řeší situaci, kdy se při přepnutí detailu do editace sice nevalidní komponenty podbarví, ale jelikož data nezměnil uživatel, tak se při pokusu o uložení nezobrazuje dialog s hláškou o chybách těchto komponent.
     */
    public async _addValidationErrosForLoadedAttrs(): Promise<void> {
        if (this.element == null) // disposed
            return;

        const f_traverse = (parent: IDomStructureElement) => {
            if (this.element == null) // disposed
                return;

            // rekurzivni prochazeni potomku
            if (parent.childControls.length > 0 && parent.control.visible) {
                for (let i = 0; i < parent.childControls.length; i++) {
                    if (this.element == null) // disposed
                        return;
                    const ctrl = parent.childControls[i].control;
                    if (ctrl && ctrl instanceof IEditableControl) {
                        if (!ctrl.isValid) {
                            this._componentValidityChanged(ctrl as IValidatable & IAttributeControl, ctrl.currentValidity);
                        }
                    }
                    f_traverse(parent.childControls[i]);
                }
            }
        };

        if (this.actualObject != null)
            f_traverse(this.startOfDomStructure);
    }

    public validate(checkRecommended?: boolean, checkPostponedRequired?: boolean): Promise<boolean> {
        return this.masterFrame.validate(checkRecommended, checkPostponedRequired);
    }

    public validateAndShowMessage(): Promise<boolean> {
        return this.masterFrame.validateAndShowMessage();
    }

    /**
     * Promis s probíhajícím vyhodnocováním práv
     */
    private _rightsEvaluationChainPromise: Promise<void> = Promise.resolve();

    protected async _checkOpenInEditAndSetEditMode(): Promise<void> {
        if (this.editModeAfterObjectChanged && this._actualObjectStandardRights.canEdit) {
            this.setDetailToEditMode(true, true);
            this.editModeAfterObjectChanged = false;
            return;
        }

        if (this._actualObjectStandardRights.canEdit && (this.ownerForm.openInEdit || this.ownerForm.multiEditMode) && this._firstOpenInEdit) {
            this._firstOpenInEdit = false;
            await this.setDetailToEditMode(true, true);
            //await this._setRightsOnStandartActions(ct);
        }
    }

    protected async _setDetailToEditMode(isEditing: boolean, alsoMasterListFrame: boolean, onlyChangeEditing = false): Promise<void> {
        if (this.isEditing === isEditing)
            return;
        if (this.ownerForm && this.ownerForm.readOnly && !this.isEditing)
            return;

        this.element.classList.toggle("isEditing", isEditing);
        this._isEditing = isEditing;

        await this.waitForMastersSelectedRecordChange();
        await this.waitForActualObjectSet();

        if (this.isChanged && this.isEditing) {
            await this.createContext();
        } else if (!isEditing) {
            await this.rollbackContext();
        }

        if (alsoMasterListFrame && this.masterFrame instanceof IDetailMasterableFrame) {
            this.masterFrame.isEditing = isEditing;

            if (this.masterFrame.isEditing !== isEditing) {
                if (this._masterFrameObjectChangedSetToEditHandler && this.masterFrame)
                    this.masterFrame.actualObjectChanged.remove(this._masterFrameObjectChangedSetToEditHandler);

                this.masterFrame.actualObjectChanged.addPredicateLimitedListener(this._masterFrameObjectChangedSetToEditHandler = (_masterFrame, _actualObjectChangedParams) => {
                    this.masterFrame.isEditing = isEditing && this._isEditing; // Musí platit obě zároveň, aby se nepřepl master opožděně do editace, pokud se mezitím na detailu editace zrušila.
                }, (): boolean => {
                    return !!this.masterFrame.actualObject;
                });
            }
        }

        if (this.masterFrame)
            await this.masterFrame.rightsEvaluationTask;

        // if (this.masterFrame == null || this.masterFrame.hasOpenedDetailInModal)
        //     await this._newEvaluateRights([RightType.visible, ...RightGroups.getControlRightGroupTypes(ControlRightTypesGroup.inEdit)]);
        // else
        if (onlyChangeEditing) {
            await this._newEvaluateRights([RightType.editing]);
        } else {
            await this._newEvaluateRightsGroup(ControlRightTypesGroup.inEdit);
        }

        this.editModeChanged.trigger(this, isEditing);

        if (this._isMinimized && isEditing)
            this._minimizeDetail(false);

        //if (this.startOfDomStructure != null)
        //    this._setEditState(this.startOfDomStructure, isEditing); // TODO-rites: ~old rites. Přepínat komponenty do editace jinde

        //if (!keepToolbarState)
        //    this._setFormUiActions(isEditing); // TODO-rites: ~old rites. Přepínat akce do editace jinde
    }

    /**
     * Nastavuje režim editace a podle toho zakazuje nebo povoluje akce v toolbaru
     * @param isEditing Zda se má aktivovat nebo zrušit režim editace
     * @param alsoMasterListFrame Zda se má nastavení editace propagovat dál na master frame
     * @param onlyChangeEditing Zda se má jen přepnout stav editace komponent, bez vyhodnocení práv
     */
    public async setDetailToEditMode(isEditing: boolean, alsoMasterListFrame: boolean, onlyChangeEditing = false): Promise<void> {
        await this._setDetailToEditMode(isEditing, alsoMasterListFrame, onlyChangeEditing);
    }
    //#endregion Prava

    protected _componentValidityChanged(control: IValidatable & IAttributeControl, validity: IValidationResult): void {
        this._enqueuePendingUpdateTask((async () => {
            const cancellationToken = new CancellationTokenSource([this._detailCTS.token]);
            // komponentu bez hodnoty nema vyznam kontrolovat, akorat by se v tu chvili zpracovavaly zmeny stavu, ke kterym dochazi pri zmene hodnoty a ktere nemaji vyznam
            if (!control.isValueLoaded || !this.actualObject)
                return;

            if (validity.valid) {
                this._validationManager.removeComponentErrors(this.actualObject.id, control);
                return;
            }

            let compNames: string[] = null;
            if (control instanceof Control)
                compNames = [control.componentName];

            const objectId = this.actualObject.id; // closure
            const message = validity.invalidMessage ? validity.invalidMessage : await Localization.getTextPromise(validity.invalidLocKey);
            const container = this._findComponentContainer(control instanceof Element ? control : control instanceof Control ? control.element : null);

            if (cancellationToken.token.cancellationRequested)
                return;
            this._validationManager.addComponentError(this.actualObject as any as IValidationDataObject<IValidationData>, {
                type: validity.errorType ? validity.errorType : ValidationErrorType.inputError,
                errorMessage: message,
                componentNames: compNames,
                invalidValue: validity.invalidValue,
            }, {
                control: control,
                attributeName: control.attrName,
                container: container,
                showsInvalidData: () => this.actualObject.id === objectId,
            });
        })());
    }

    private _findComponentContainer(el: Element): IValidatableContainer {
        if (!el)
            return null;
        return GenericControl.findParent(el as HTMLElement, (element) => element instanceof IValidatableContainer) as any as IValidatableContainer;
    }

    /**
     * Vrací true, pokud byl detail otevřený jako implicitní formulář.
     * Viz https://teaf.tescosw.cz/wiki/index.php?title=Implicitn%C3%AD_formul%C3%A1%C5%99
     */
    private _isImplicit(): boolean {
        return this.ownerForm.isImplicit;
    }

    public get ignoreMasterRightsObject(): boolean {
        return !this._shouldUseRightsObjectFromMasterFrame();
    }

    /**
     * Rozhoduje, zda DetailFrame používá objekt pro základní práva ze svého master frame, nebo zda má vlastní.
     */
    private _shouldUseRightsObjectFromMasterFrame(): boolean {
        if (!this.masterFrame) { // Pokud nemá masterFrame, nemůže z něj brát objekt práv
            return false;
        }

        if (!this.ownerForm.detailFormMasterFrame) { // Značí, že je DetailFrame na stejném Formu, jako jeho masterFrame. Přebere tedy od něj objekt práv.
            return true;
        }

        if (this._isImplicit()) { // Pokud jde o DetailFrame na IMPLICITně otevřeném Formu, může být nad jinou třídou, použije tedy vlastní objekt práv.
            return false;
        }

        if (this._insertObjectName) { // Pokud je DetailForm na modálním Formu otevřeném pomocí ShowInsertAction, může být nad jinou třídou než jeho masterFrame, použije tedy vlastní objekt práv.
            return false;
        }

        return true;
        //return this.masterFrame && (!this.ownerForm.detailFormMasterFrame || !this.isImplicit());
    }

    protected _getRequestContext(cancellationToken: CancellationToken = CancellationToken.none): RequestContext {
        return convertContextFromFrameToRequest(this.frameContext, cancellationToken);
    }

    /**
     * Třídy kompozitních atributů.
     */
    public get composites(): string {
        return this.masterFrame?.composites;
    }
}

/**
 * Datový interface s daty potřebnými k vyhodnocení práv nad jednou konkrétní komponentou
 */
export interface IControlRightsData {
    /** struktura s daty jedné komponenty */
    aktElement: IDomStructureElement;
    /** výsledek vyhodnocení práv */
    itemsRightResult?: Map<string, IClassItemRights>;
    /** výsledek vyhodnocení práv */
    evaluatedConditions: IEvaluatedConditionResult[];
    /** název metody nebo atributu */
    itemName?: string;
    /** zda má být vyhodnoceno bez ActualObjectu */
    applyConditionResults: boolean;
    enable?: boolean;
    visible?: boolean;
}
